<template>
  <section class="p-4 md:p-6">
    <UCard
      :ui="{
        body: 'space-y-4',
        header: 'border-b border-gray-700',
      }"
    >
      <template #header>
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <UIcon
              name="material-symbols-light:account-tree-outline-rounded"
              class="text-xl"
            />
            <h2 class="text-lg font-semibold">Workflow 管理</h2>
          </div>
          <UButton
            icon="material-symbols-light:refresh-rounded"
            color="neutral"
            variant="soft"
            size="sm"
            :loading="loadingAll"
            @click="refreshAll"
          >
            刷新
          </UButton>
        </div>
      </template>

      <div class="grid items-stretch gap-4 lg:h-[720px] lg:grid-cols-4">
        <UCard
          :ui="{
            body: 'min-h-0 flex-1 space-y-4 overflow-y-auto custom-scroll pr-1',
            header: 'shrink-0 border-b border-gray-700',
          }"
          class="flex h-full min-h-0 flex-col overflow-hidden bg-gray-900/40"
        >
          <template #header>
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div class="text-base font-semibold text-white">执行控制</div>
              </div>
            </div>
          </template>

          <div class="space-y-3">
            <div class="space-y-2">
              <div class="text-xs text-gray-400">执行模式</div>
              <UTabs
                v-model="processKind"
                :items="processKindItems"
                value-key="value"
                :content="false"
                size="sm"
                color="primary"
                variant="pill"
              />
            </div>

            <!-- <div
              class="rounded-lg border border-gray-700 bg-black/20 p-3 text-xs text-gray-300"
            >
              <div v-if="processKind === 'manus_new_session'">
                Manus 新会话处理固定为全量执行：创建或运行新的 Manus 结果后，后续步骤全部重新生成。
              </div>
              <div v-else class="space-y-1">
                <div>Manus 更新处理固定为按勾选控制：</div>
                <div>
                  `重新执行 Manus 处理` 只决定是否向已有 Manus task 发送更新提示词。
                </div>
                <div>
                  后面三个勾选只决定是否强制重新生成；未勾选时会自动比对输入参数，没变化就复用历史产物。
                </div>
              </div>
            </div> -->
          </div>

          <div
            v-if="processKind === 'manus_update'"
            class="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3"
          >
            <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div class="text-sm font-medium text-amber-200">步骤开关</div>
              <UButton
                size="xs"
                color="neutral"
                variant="soft"
                icon="material-symbols-light:account-tree-outline-rounded"
                @click="workflowDagOpen = true"
              >
                查看流程图
              </UButton>
            </div>
            <div class="space-y-3">
              <label class="flex items-center gap-2 text-sm text-gray-200">
                <UCheckbox v-model="partialFlags.manus_enabled" />
                更新 Manus 结果
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-200">
                <UCheckbox v-model="partialFlags.bgm_enabled" />
                重新处理 BGM
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-200">
                <UCheckbox v-model="partialFlags.html_enabled" />
                重新下载 HTML / page_config
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-200">
                <UCheckbox v-model="partialFlags.image_enabled" />
                重新文生图
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-200">
                <UCheckbox v-model="partialFlags.first_frame_enabled" />
                重新首帧生成
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-200">
                <UCheckbox v-model="partialFlags.tail_frame_enabled" />
                重新尾帧生成
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-200">
                <UCheckbox v-model="partialFlags.video_enabled" />
                重新图生视频
              </label>
            </div>
            <div class="mt-2 text-xs text-gray-400">
              {{ partialExecutionSummary }}
            </div>
          </div>

          <div
            v-if="shouldShowUpdatePrompt"
            class="rounded-lg border border-gray-700 bg-black/20 p-3 space-y-2"
          >
            <div class="text-sm font-medium text-white">更新处理提示词</div>
            <UTextarea
              v-model="updatePrompt"
              :rows="4"
              placeholder="可选：输入后发送给 Manus；留空则直接使用数据库中最新回调"
              class="w-full"
            />
            <div class="text-xs text-gray-400">
              留空时不会发送新消息，会直接读取该 Manus task
              在数据库保存的最新回调。
            </div>
          </div>

          <div
            v-if="
              processKind === 'manus_update' && selectedUpdateMissingManusCount
            "
            class="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-200"
          >
            当前选中的任务里有 {{ selectedUpdateMissingManusCount }} 个没有
            `Manus Task id`，不能执行已有会话增量执行。
          </div>

          <div class="flex flex-col items-stretch gap-2">
            <div class="grid grid-cols-[112px_1fr] items-end gap-2">
              <label class="flex min-w-0 flex-col gap-1 text-xs text-gray-400">
                <span>并发数量</span>
                <UInput
                  v-model.number="concurrencyLimit"
                  type="number"
                  min="1"
                  max="20"
                  size="sm"
                />
              </label>
              <UButton
                icon="material-symbols-light:play-circle-outline-rounded"
                color="primary"
                :loading="creatingBatch"
                :disabled="!canCreateBatch"
                block
                @click="createBatch"
              >
                开始处理 {{ selectedCount }} 个任务
              </UButton>
            </div>
            <div
              class="rounded-md border border-gray-700/80 bg-black/20 px-2.5 py-2 text-xs"
            >
              <div class="mb-1 text-gray-500">已选 template_code</div>
              <div
                v-if="selectedTemplateCodesPreview.length"
                class="max-h-24 overflow-y-auto font-mono text-gray-200 break-all whitespace-pre-wrap"
              >
                {{ selectedTemplateCodesPreview.join(", ") }}
              </div>
              <div v-else class="text-gray-500">暂无，请在右侧任务源中勾选</div>
            </div>
            <span v-if="batchActionMessage" class="text-sm text-gray-300">
              {{ batchActionMessage }}
            </span>
          </div>
        </UCard>

        <UCard
          :ui="{
            body: 'flex min-h-0 flex-1 flex-col space-y-4',
            header: 'shrink-0 border-b border-gray-700',
          }"
          class="flex h-full min-h-0 flex-col overflow-hidden bg-gray-900/40 lg:col-span-3"
        >
          <template #header>
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div class="flex items-center gap-2">
                <div class="text-base font-semibold text-white">任务源管理</div>
                <span class="text-xs text-gray-500"
                  >可新增 / 编辑 / 勾选执行</span
                >
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <UButton
                  size="sm"
                  color="neutral"
                  variant="soft"
                  @click="toggleSelectAll(true)"
                >
                  全选
                </UButton>
                <UButton
                  size="sm"
                  color="neutral"
                  variant="soft"
                  @click="toggleSelectAll(false)"
                >
                  清空
                </UButton>
                <UButton
                  size="sm"
                  color="neutral"
                  variant="soft"
                  icon="material-symbols-light:category-outline-rounded"
                  @click="openCategoryModal"
                >
                  分类管理
                </UButton>
                <UButton
                  v-if="canShowPublish"
                  size="sm"
                  color="success"
                  variant="solid"
                  :loading="
                    publishingKey ===
                    `${EFFECTS_PUBLISH_RESOURCE}:workflow-batch`
                  "
                  :disabled="!canBatchPublishSelectedSources || !!publishingKey"
                  :title="selectedPublishBlockReason || ''"
                  @click="batchPublishSelectedSources"
                >
                  {{
                    getPublishProgressText(
                      `${EFFECTS_PUBLISH_RESOURCE}:workflow-batch`
                    ) || "批量上线"
                  }}
                </UButton>
                <UButton
                  size="sm"
                  color="primary"
                  variant="soft"
                  @click="openCreateForm"
                >
                  新建任务
                </UButton>
                <UInput
                  v-model="keyword"
                  size="sm"
                  icon="material-symbols-light:search-rounded"
                  placeholder="搜索标题 / code / 分类"
                  class="w-56"
                />
                <UButton
                  v-if="sourceFormExpanded"
                  size="sm"
                  color="neutral"
                  variant="soft"
                  @click="resetForm"
                >
                  收起表单
                </UButton>
              </div>
            </div>
          </template>

          <div
            v-if="sourceFormExpanded"
            class="rounded-lg border border-gray-700 bg-black/20 p-4 space-y-4"
          >
            <div class="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-x-6">
              <label class="flex min-w-0 flex-col gap-1 text-xs text-gray-400">
                <span>Effect 名称</span>
                <UInput
                  v-model="form.effect_title"
                  class="w-full"
                  placeholder="Effect 名称"
                />
              </label>
              <label class="flex min-w-0 flex-col gap-1 text-xs text-gray-400">
                <span>Template Code</span>
                <UInput
                  v-model="form.template_code"
                  class="w-full"
                  placeholder="可留空自动生成"
                />
                <span
                  v-if="duplicateTemplateCodeSource"
                  class="text-xs text-amber-400"
                >
                  template_code「{{
                    resolvedFormTemplateCode
                  }}」已存在，对应任务：{{
                    duplicateTemplateCodeSource.effect_title || "未命名"
                  }}
                </span>
                <span
                  v-else-if="formTemplateCodeAutoGenerated"
                  class="text-xs text-gray-500"
                >
                  留空将根据名称自动生成：{{ resolvedFormTemplateCode }}
                </span>
              </label>
              <label
                class="flex min-w-0 flex-col gap-1 text-xs text-gray-400 md:col-span-2"
              >
                <span>原作品视频链接</span>
                <UInput
                  v-model="form.source_video_url"
                  class="w-full"
                  placeholder="原作品视频链接"
                />
              </label>
              <label
                class="flex min-w-0 flex-col gap-1 text-xs text-gray-400 md:col-span-2"
              >
                <span>源封面视频链接</span>
                <UInput
                  v-model="form.source_cover_url"
                  class="w-full"
                  placeholder="可选：封面视频 mp4/webm 等"
                />
              </label>
              <label
                class="flex min-w-0 flex-col gap-1 text-xs text-gray-400 md:col-span-2"
              >
                <span>分类</span>
                <div class="flex flex-wrap items-center gap-2">
                  <USelectMenu
                    v-model="selectedCategoryOption"
                    class="min-w-0 flex-1"
                    :items="categorySelectItems"
                    value-key="value"
                    multiple
                    placeholder="选择分类（支持多选，需先在分类管理中新增）"
                    searchable
                  />
                  <UButton
                    size="sm"
                    color="neutral"
                    variant="soft"
                    icon="material-symbols-light:category-outline-rounded"
                    @click="openCategoryModal"
                  >
                    管理分类
                  </UButton>
                </div>
              </label>
              <label class="flex min-w-0 flex-col gap-1 text-xs text-gray-400">
                <span>素材数</span>
                <UInput
                  v-model.number="form.assets_num"
                  class="w-full"
                  type="number"
                  min="1"
                  placeholder="素材数"
                />
              </label>
              <label
                class="flex min-w-0 flex-col gap-1 text-xs text-gray-400 md:col-span-2"
              >
                <span>来源</span>
                <div class="grid gap-2 md:grid-cols-[minmax(0,1fr)_220px]">
                  <UInput
                    v-model="form.notes"
                    class="w-full"
                    placeholder="输入来源"
                  />
                  <USelectMenu
                    v-model="selectedSourceOriginOption"
                    class="w-full"
                    :items="formSourceOriginSelectItems"
                    value-key="value"
                    placeholder="选择已有"
                    searchable
                  />
                </div>
              </label>
            </div>

            <div class="flex flex-wrap items-center gap-2">
              <UButton
                :loading="savingSource"
                :disabled="!!duplicateTemplateCodeSource"
                icon="material-symbols-light:save-outline-rounded"
                @click="saveSource"
              >
                {{ form.id ? "保存修改" : "新增任务" }}
              </UButton>
              <UButton color="neutral" variant="soft" @click="resetForm">
                取消
              </UButton>
              <span v-if="sourceActionMessage" class="text-sm text-gray-300">
                {{ sourceActionMessage }}
              </span>
            </div>
          </div>

          <div
            v-if="sourceKeywordFilteredSources.length"
            class="flex shrink-0 flex-col gap-3 border-b border-gray-800 pb-2 lg:flex-row lg:items-center lg:justify-between"
          >
            <div class="flex min-w-0 flex-wrap items-center gap-2">
              <USelectMenu
                v-model="sourceStatusFilter"
                :items="sourceStatusItems"
                value-key="value"
                size="xs"
                class="min-w-[150px]"
              />
              <USelectMenu
                v-model="sourceCategoryFilter"
                :items="sourceCategoryItems"
                value-key="value"
                size="xs"
                class="min-w-[170px]"
              />
              <USelectMenu
                v-if="canShowPublish"
                v-model="publishStatusFilter"
                :items="publishStatusItems"
                value-key="value"
                size="xs"
                class="min-w-[170px]"
              />
              <USelectMenu
                v-model="sourceNotesFilter"
                :items="sourceNotesItems"
                value-key="value"
                size="xs"
                class="min-w-[170px]"
              />
              <UButton
                size="xs"
                color="neutral"
                variant="soft"
                @click="resetSourceFilters"
              >
                重置
              </UButton>
            </div>
            <div
              class="flex shrink-0 flex-wrap items-center gap-3 lg:justify-end"
            >
              <div class="text-xs text-gray-500">
                共 {{ filteredSources.length }} 条 · 第 {{ sourcePage }} /
                {{ sourcePageCount }} 页
              </div>
              <UPagination
                v-if="showSourcePagination"
                v-model:page="sourcePage"
                :total="filteredSources.length"
                :items-per-page="sourceItemsPerPage"
                size="xs"
                color="primary"
                variant="subtle"
                :sibling-count="1"
                @update:page="onSourcePageChange"
              />
            </div>
          </div>
          <div
            v-if="canShowPublish && effectsConfigsError"
            class="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200"
          >
            上线状态加载失败，请刷新重试：{{ effectsConfigsError }}
          </div>

          <div
            ref="sourceListRef"
            class="min-h-0 flex-1 space-y-3 overflow-y-auto custom-scroll pr-1"
          >
            <div v-if="loadingSources && !sources.length" class="space-y-3">
              <div
                v-for="index in 4"
                :key="`source-skeleton-${index}`"
                class="rounded-lg border border-gray-700 bg-black/20 p-3 space-y-3 animate-pulse"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="flex items-start gap-3 flex-1">
                    <div class="mt-1 h-4 w-4 rounded bg-gray-700"></div>
                    <div class="flex-1 space-y-2">
                      <div class="flex flex-wrap items-center gap-2">
                        <div class="h-4 w-32 rounded bg-gray-700"></div>
                        <div class="h-4 w-20 rounded bg-gray-800"></div>
                        <div class="h-4 w-16 rounded bg-gray-800"></div>
                      </div>
                      <div class="h-3 w-48 rounded bg-gray-800"></div>
                      <div class="h-3 w-full rounded bg-gray-800"></div>
                      <div class="h-3 w-40 rounded bg-gray-800"></div>
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <div class="h-7 w-12 rounded bg-gray-800"></div>
                    <div class="h-7 w-12 rounded bg-gray-800"></div>
                    <div class="h-7 w-12 rounded bg-gray-800"></div>
                  </div>
                </div>
              </div>
            </div>
            <template v-else-if="filteredSources.length">
              <div
                v-for="item in pagedSources"
                :key="item.id"
                class="relative rounded-lg border p-3 space-y-3 transition-all"
                :class="
                  selectedIds.includes(item.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-700 bg-black/20'
                "
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="flex min-w-0 flex-1 items-start gap-3">
                    <UCheckbox
                      :model-value="selectedIds.includes(item.id)"
                      @update:model-value="toggleSelected(item.id, $event)"
                    />
                    <div class="min-w-0 w-full space-y-1">
                      <div class="flex flex-wrap items-center gap-2 pr-20">
                        <div class="text-sm font-semibold text-white">
                          {{ item.effect_title }}
                        </div>
                        <UBadge
                          v-if="sourceDisplayStatus(item)"
                          :color="
                            statusColorMap[sourceDisplayStatus(item)] ||
                            'neutral'
                          "
                          variant="subtle"
                        >
                          {{ sourceDisplayStatus(item) }}
                        </UBadge>
                      </div>
                      <!-- 以下信息左右两列布局：左侧文字，右侧媒体组件靠右居右 -->
                      <div
                        class="flex w-full min-w-0 items-start justify-between gap-4"
                      >
                        <!-- 左侧：文字信息，靠左 -->
                        <div class="min-w-0 flex-1 space-y-1 text-left">
                          <div>
                            <code
                              class="block text-xs text-cyan-300 whitespace-pre-line break-all"
                            >
                              {{ item.template_code }}
                            </code>
                          </div>
                          <div class="text-xs text-gray-400">
                            分类 {{ item.category_name || "-" }} /
                            {{ item.template_category || "-" }} · assets
                            {{ item.assets_num || 1 }}
                          </div>
                          <div
                            v-if="item.latest_manus_task_id"
                            class="flex flex-wrap items-center gap-2 text-xs text-amber-300"
                          >
                            <span class="min-w-0 break-all"
                              >Manus Task: {{ item.latest_manus_task_id }}</span
                            >
                            <UButton
                              size="xs"
                              color="primary"
                              variant="soft"
                              icon="material-symbols-light:open-in-new-rounded"
                              :to="manusAppUrl(item.latest_manus_task_id)"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Manus
                            </UButton>
                          </div>
                          <div class="text-xs text-gray-400">
                            更新时间: {{ formatDateTime(item.updated_at) }}
                          </div>
                        </div>
                        <!-- 右侧：媒体组件，横向排列，靠右 -->
                        <div
                          v-if="
                            (sourcePreviewTilesMap[item.id] || []).length ||
                            canOpenSourceLinks(item)
                          "
                          class="ml-auto flex min-w-0 max-w-[60%] flex-row flex-wrap items-start justify-end gap-2.5"
                        >
                          <div
                            v-for="tile in sourcePreviewTilesMap[item.id] || []"
                            :key="`${item.id}-${tile.key}`"
                            class="flex flex-col items-center gap-1"
                          >
                            <button
                              type="button"
                              class="relative h-16 w-16 shrink-0 overflow-hidden rounded-md border border-gray-600 bg-gray-950 ring-offset-2 ring-offset-gray-900 transition hover:ring-2 hover:ring-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                              :title="tile.label"
                              @click="openAssetDetail(tile, item)"
                            >
                              <video
                                v-if="tile.isVideo"
                                :src="tile.url"
                                :key="tile.url"
                                muted
                                playsinline
                                preload="auto"
                                class="pointer-events-none h-full w-full object-cover"
                              />
                              <img
                                v-else
                                :src="tile.url"
                                alt=""
                                loading="eager"
                                decoding="async"
                                class="pointer-events-none h-full w-full object-cover"
                              />
                            </button>
                            <div
                              class="flex max-w-20 items-center justify-center gap-1"
                            >
                              <span
                                class="min-w-0 text-center text-[10px] leading-tight text-gray-500"
                                >{{ tile.label }}</span
                              >
                              <UCheckbox
                                v-if="tile.manusSelectable"
                                :model-value="
                                  item.manus_video_source ===
                                  tile.manusVideoSource
                                "
                                :disabled="
                                  Boolean(savingManusVideoSourceMap[item.id])
                                "
                                :title="`发送 ${tile.label} 给 Manus`"
                                size="xs"
                                @click.stop
                                @update:model-value="
                                  setSourceManusVideoSource(
                                    item,
                                    tile.manusVideoSource,
                                    $event
                                  )
                                "
                              />
                            </div>
                          </div>
                          <div
                            v-if="canOpenSourceLinks(item)"
                            class="flex h-16 flex-wrap items-center justify-end gap-1.5"
                          >
                            <UPopover arrow>
                              <UButton
                                size="xs"
                                color="primary"
                                variant="soft"
                                trailing-icon="material-symbols-light:expand-more-rounded"
                              >
                                产品页
                              </UButton>
                              <template #content>
                                <div class="flex min-w-36 flex-col gap-0.5 p-1">
                                  <UButton
                                    size="xs"
                                    color="primary"
                                    variant="ghost"
                                    class="justify-start"
                                    icon="material-symbols-light:open-in-new-rounded"
                                    :to="sourceProductPageUrl(item, 'test')"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    测试环境
                                  </UButton>
                                  <UButton
                                    size="xs"
                                    color="primary"
                                    variant="ghost"
                                    class="justify-start"
                                    icon="material-symbols-light:open-in-new-rounded"
                                    :to="sourceProductPageUrl(item, 'prod')"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    生产环境
                                  </UButton>
                                </div>
                              </template>
                            </UPopover>
                            <UPopover arrow>
                              <UButton
                                size="xs"
                                color="neutral"
                                variant="soft"
                                icon="material-symbols-light:code-blocks-outline-rounded"
                                trailing-icon="material-symbols-light:expand-more-rounded"
                              >
                                HTML
                              </UButton>
                              <template #content>
                                <div class="flex min-w-36 flex-col gap-0.5 p-1">
                                  <UButton
                                    size="xs"
                                    color="neutral"
                                    variant="ghost"
                                    class="justify-start"
                                    icon="material-symbols-light:open-in-new-rounded"
                                    :to="sourceHtmlPageUrl(item, 'test')"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    测试环境
                                  </UButton>
                                  <UButton
                                    size="xs"
                                    color="neutral"
                                    variant="ghost"
                                    class="justify-start"
                                    icon="material-symbols-light:open-in-new-rounded"
                                    :to="sourceHtmlPageUrl(item, 'prod')"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    生产环境
                                  </UButton>
                                </div>
                              </template>
                            </UPopover>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="absolute top-3 right-3 z-10 flex items-center gap-1.5"
                  >
                    <UButton
                      v-if="canShowPublish"
                      size="xs"
                      :color="
                        sourcePublishMeta(item).state === 'publishable'
                          ? 'success'
                          : sourcePublishMeta(item).color
                      "
                      variant="solid"
                      :loading="
                        publishingKey ===
                        `${EFFECTS_PUBLISH_RESOURCE}:workflow:${item.id}`
                      "
                      :disabled="
                        sourcePublishMeta(item).disabled || !!publishingKey
                      "
                      :title="sourcePublishMeta(item).reason || ''"
                      @click="publishSource(item)"
                    >
                      {{ sourcePublishMeta(item).label }}
                    </UButton>
                    <UButton
                      size="xs"
                      color="neutral"
                      variant="soft"
                      icon="material-symbols-light:edit-outline-rounded"
                      title="编辑"
                      aria-label="编辑"
                      @click="editSource(item)"
                    />
                    <UButton
                      size="xs"
                      color="error"
                      variant="soft"
                      icon="material-symbols-light:delete-outline-rounded"
                      title="删除"
                      aria-label="删除"
                      :loading="deletingSourceId === item.id"
                      @click="deleteSource(item)"
                    />
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="text-sm text-gray-400">当前没有任务源</div>
          </div>
        </UCard>
      </div>

      <div class="grid gap-4 lg:grid-cols-4">
        <UCard
          :ui="{ body: 'space-y-3', header: 'border-b border-gray-700' }"
          class="bg-gray-900/40"
        >
          <template #header>
            <div class="flex items-center justify-between gap-2">
              <div class="text-base font-semibold text-white">最近批次</div>
              <div class="flex items-center gap-2">
                <UIcon
                  v-if="loadingBatches && batches.length"
                  name="i-lucide-loader-2"
                  class="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400"
                  title="批次列表自动刷新中"
                />
                <div class="text-xs text-gray-400">保留最近 50 条</div>
              </div>
            </div>
            <div
              v-if="batches.length"
              class="mt-3 flex flex-wrap items-center gap-2"
            >
              <UCheckbox
                :model-value="isAllPurgeableBatchesSelected"
                :disabled="!purgeableBatches.length || batchPurging"
                label="全选可清理"
                @update:model-value="toggleSelectAllBatches"
              />
              <UButton
                size="xs"
                color="warning"
                variant="soft"
                icon="material-symbols-light:delete-outline-rounded"
                :loading="batchPurging"
                :disabled="!selectedPurgeableBatchCount || batchPurging"
                @click="purgeSelectedBatches"
              >
                批量清理{{
                  selectedPurgeableBatchCount
                    ? ` (${selectedPurgeableBatchCount})`
                    : ""
                }}
              </UButton>
            </div>
          </template>

          <div
            class="space-y-2 max-h-[980px] overflow-y-auto custom-scroll pr-1"
          >
            <div v-if="loadingBatches && !batches.length" class="space-y-2">
              <div
                v-for="index in 4"
                :key="`batch-skeleton-${index}`"
                class="rounded-lg border border-gray-700 bg-black/20 p-3 space-y-2 animate-pulse"
              >
                <div class="flex items-center justify-between gap-2">
                  <div class="h-4 w-28 rounded bg-gray-700"></div>
                  <div class="h-5 w-14 rounded bg-gray-800"></div>
                </div>
                <div class="h-3 w-32 rounded bg-gray-800"></div>
                <div class="space-y-1">
                  <div class="h-3 w-40 rounded bg-gray-800"></div>
                  <div class="h-3 w-40 rounded bg-gray-800"></div>
                  <div class="h-3 w-40 rounded bg-gray-800"></div>
                </div>
                <div class="h-3 w-full rounded bg-gray-800"></div>
                <div class="h-7 w-24 rounded bg-gray-800"></div>
              </div>
            </div>
            <template v-else-if="batches.length">
              <div
                v-for="batch in batches"
                :key="batch.id"
                class="rounded-lg border p-3 space-y-2 transition-all"
                :class="
                  selectedBatchIds.includes(batch.id)
                    ? 'border-warning bg-warning/5'
                    : 'border-gray-700 bg-black/20'
                "
              >
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div class="flex min-w-0 items-start gap-2">
                    <UCheckbox
                      v-if="isBatchPurgeable(batch)"
                      :model-value="selectedBatchIds.includes(batch.id)"
                      :disabled="batchPurging"
                      @update:model-value="
                        toggleBatchSelected(batch.id, $event)
                      "
                    />
                    <div
                      v-else
                      class="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-gray-700 bg-gray-900/60"
                      title="排队或运行中的批次需先终止后再清理"
                    />
                    <div class="min-w-0 space-y-1">
                      <div class="text-sm font-medium text-white">
                        {{ batchKindLabel(batch.process_kind) }} ·
                        {{ batchRunModeLabel(batch.trigger_mode) }}
                      </div>
                      <div class="text-xs text-gray-500">
                        ID: {{ batch.id }}
                      </div>
                    </div>
                  </div>
                  <UBadge
                    :color="statusColorMap[batch.status] || 'neutral'"
                    variant="subtle"
                  >
                    {{ batch.status }}
                  </UBadge>
                </div>
                <div class="text-xs text-gray-400">
                  本批任务 {{ batchTaskTotal(batch) }} 个 · 并发
                  {{ batch.concurrency_limit }} · 创建人
                  {{ batch.created_by || "-" }}
                </div>
                <div class="text-xs text-gray-500 space-y-1">
                  <div>创建时间 {{ formatDateTime(batch.created_at) }}</div>
                  <div>开始时间 {{ formatDateTime(batch.started_at) }}</div>
                  <div>结束时间 {{ formatDateTime(batch.finished_at) }}</div>
                </div>
                <div class="text-xs text-gray-400">
                  排队 {{ batch.stats?.queued || 0 }} · 运行
                  {{ batch.stats?.running || 0 }} · 成功
                  {{ batch.stats?.succeeded || 0 }} · 失败
                  {{ batch.stats?.failed || 0 }} · 取消
                  {{ batch.stats?.cancelled || 0 }} · 跳过
                  {{ batch.stats?.skipped || 0 }}
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <UButton
                    size="xs"
                    color="neutral"
                    variant="soft"
                    @click="focusBatch(batch.id)"
                  >
                    查看该批次任务
                  </UButton>
                  <UButton
                    v-if="isBatchCancelable(batch)"
                    size="xs"
                    color="error"
                    variant="soft"
                    :loading="cancellingBatchId === batch.id"
                    :disabled="batch.cancel_requested"
                    @click="cancelBatch(batch)"
                  >
                    {{ batch.cancel_requested ? "终止中" : "终止批次" }}
                  </UButton>
                  <UButton
                    v-if="isBatchPurgeable(batch)"
                    size="xs"
                    color="warning"
                    variant="soft"
                    icon="material-symbols-light:delete-outline-rounded"
                    :loading="purgingBatchId === batch.id"
                    title="清理批次"
                    aria-label="清理批次"
                    @click="purgeBatch(batch)"
                  />
                </div>
              </div>
            </template>
            <div v-else class="text-sm text-gray-400">暂无批次记录</div>
          </div>
        </UCard>

        <UCard
          :ui="{ body: 'space-y-4', header: 'border-b border-gray-700' }"
          class="bg-gray-900/40 lg:col-span-3"
        >
          <template #header>
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div class="text-base font-semibold text-white">
                  运行任务与日志
                </div>
              </div>
              <div class="flex flex-wrap items-center justify-end gap-2">
                <UIcon
                  v-if="loadingRunTasks && runTasks.length"
                  name="i-lucide-loader-2"
                  class="h-4 w-4 shrink-0 animate-spin text-gray-400"
                  title="列表自动刷新中"
                />
                <UInput
                  v-model="runTaskBatchFilterDraft"
                  size="sm"
                  placeholder="按 batch_id 查看"
                  class="w-44"
                />
                <UButton
                  size="sm"
                  color="primary"
                  variant="soft"
                  @click="queryRunTasksByBatch"
                >
                  查询
                </UButton>
                <UButton
                  size="sm"
                  color="neutral"
                  variant="soft"
                  @click="resetRunTasksBatchFilter"
                >
                  重置
                </UButton>
              </div>
            </div>
          </template>

          <div
            class="space-y-3 max-h-[980px] overflow-y-auto custom-scroll pr-1"
          >
            <div v-if="loadingRunTasks && !runTasks.length" class="space-y-3">
              <div
                v-for="index in 4"
                :key="`task-skeleton-${index}`"
                class="rounded-lg border border-gray-700 bg-black/20 p-3 space-y-3 animate-pulse"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="flex-1 space-y-2">
                    <div class="flex items-center gap-2">
                      <div class="h-4 w-24 rounded bg-gray-700"></div>
                      <div class="h-5 w-14 rounded bg-gray-800"></div>
                      <div class="h-3 w-20 rounded bg-gray-800"></div>
                    </div>
                    <div class="h-3 w-40 rounded bg-gray-800"></div>
                    <div class="space-y-1">
                      <div class="h-3 w-48 rounded bg-gray-800"></div>
                      <div class="h-3 w-48 rounded bg-gray-800"></div>
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <div class="h-7 w-16 rounded bg-gray-800"></div>
                    <div class="h-7 w-16 rounded bg-gray-800"></div>
                  </div>
                </div>
                <div class="space-y-2">
                  <div class="h-3 w-full rounded bg-gray-800"></div>
                  <div class="h-3 w-5/6 rounded bg-gray-800"></div>
                </div>
              </div>
            </div>
            <template v-else-if="runTasks.length">
              <div
                v-for="task in runTasks"
                :key="task.id"
                class="rounded-lg border border-gray-700 bg-black/20 p-3 space-y-3"
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="space-y-1">
                    <div class="flex flex-wrap items-center gap-2">
                      <div class="text-sm font-semibold text-white">
                        {{ task.source_code }}
                      </div>
                      <UBadge
                        :color="statusColorMap[task.status] || 'neutral'"
                        variant="subtle"
                      >
                        {{ task.status }}
                      </UBadge>
                      <span
                        v-if="task.status === 'running' && task.current_step"
                        class="text-xs text-cyan-300"
                      >
                        当前步骤：{{ task.current_step }}
                      </span>
                      <span
                        v-if="
                          task.cancel_requested && task.status === 'running'
                        "
                        class="text-xs text-amber-300"
                      >
                        终止中
                      </span>
                    </div>
                    <div class="text-xs text-gray-400 break-all">
                      task_id: {{ task.id }}
                    </div>
                    <div class="text-xs text-gray-500 break-all space-y-1">
                      <div>创建时间 {{ formatDateTime(task.created_at) }}</div>
                      <div>开始时间 {{ formatDateTime(task.started_at) }}</div>
                      <div>结束时间 {{ formatDateTime(task.finished_at) }}</div>
                    </div>
                    <div
                      v-if="task.manus_task_id"
                      class="flex flex-wrap items-center gap-2 text-xs text-amber-300"
                    >
                      <span class="min-w-0 break-all"
                        >manus_task_id: {{ task.manus_task_id }}</span
                      >
                      <UButton
                        size="xs"
                        color="primary"
                        variant="soft"
                        icon="material-symbols-light:open-in-new-rounded"
                        :to="manusAppUrl(task.manus_task_id)"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Manus
                      </UButton>
                    </div>
                    <div
                      v-if="task.error_message"
                      class="text-xs text-red-300 break-all whitespace-pre-wrap"
                      style="word-break: break-all; white-space: pre-wrap"
                    >
                      {{ task.error_message }}
                    </div>
                  </div>
                  <div class="flex flex-wrap items-center justify-end gap-2">
                    <UIcon
                      v-if="
                        loadingRunTasks &&
                        runTasks.length &&
                        ['queued', 'running'].includes(task.status)
                      "
                      name="i-lucide-loader-2"
                      class="h-3.5 w-3.5 shrink-0 animate-spin text-gray-500"
                      title="任务状态自动刷新中"
                    />
                    <UButton
                      size="xs"
                      color="neutral"
                      variant="soft"
                      @click="openRunTaskLog(task)"
                    >
                      查看日志
                    </UButton>
                    <UButton
                      size="xs"
                      color="neutral"
                      variant="soft"
                      @click="refreshTaskDetail(task.id)"
                    >
                      刷新详情
                    </UButton>
                    <UButton
                      v-if="isTaskCancelable(task)"
                      size="xs"
                      color="error"
                      variant="soft"
                      :loading="cancellingTaskId === task.id"
                      :disabled="task.cancel_requested"
                      @click="cancelTask(task)"
                    >
                      {{ task.cancel_requested ? "终止中" : "终止任务" }}
                    </UButton>
                  </div>
                </div>

                <div v-if="task.asset" class="grid gap-2 text-xs text-gray-300">
                  <a
                    v-if="task.asset.new_cover_url"
                    :href="task.asset.new_cover_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hover:text-primary break-all"
                  >
                    新封面：{{ task.asset.new_cover_url }}
                  </a>
                  <a
                    v-if="task.asset.compressed_video_url"
                    :href="task.asset.compressed_video_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hover:text-primary break-all"
                  >
                    成品视频：{{ task.asset.compressed_video_url }}
                  </a>
                </div>

                <div
                  v-if="hasLoadedTaskSteps(task)"
                  class="rounded-lg border border-gray-800 bg-gray-950/40 p-2.5"
                >
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      v-for="step in runTaskTimeline(task)"
                      :key="`${task.id}-${step.key}`"
                      type="button"
                      class="inline-flex min-h-7 items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors"
                      :class="timelineStepClass(step.state)"
                      @click="openRunTaskLog(task, step.key)"
                    >
                      <span
                        class="h-1.5 w-1.5 shrink-0 rounded-full"
                        :class="timelineDotClass(step.state)"
                      />
                      <span class="max-w-24 truncate">{{
                        step.shortLabel
                      }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="text-sm text-gray-400">当前没有运行任务记录</div>
          </div>
        </UCard>
      </div>
    </UCard>

    <UModal
      v-model:open="logModalOpen"
      :ui="{ content: 'max-w-6xl max-h-[90vh] overflow-hidden' }"
      @update:open="onLogModalUpdateOpen"
    >
      <template #content>
        <UCard
          class="max-h-[90vh] overflow-hidden bg-gray-900 ring-1 ring-gray-700"
          :ui="{ body: 'max-h-[90vh] overflow-y-auto custom-scroll p-6' }"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0 space-y-1">
              <div class="flex flex-wrap items-center gap-2">
                <div class="text-base font-semibold text-white">
                  {{ activeLogTask?.source_code || "运行任务日志" }}
                </div>
                <UBadge
                  v-if="activeLogTask?.status"
                  :color="statusColorMap[activeLogTask.status] || 'neutral'"
                  variant="subtle"
                >
                  {{ activeLogTask.status }}
                </UBadge>
                <UIcon
                  v-if="logDetailLoading || logFetchLoading[activeLogTaskId]"
                  name="i-lucide-loader-2"
                  class="h-4 w-4 animate-spin text-gray-400"
                  title="日志加载中"
                />
                <div
                  v-else-if="
                    activeLogTaskId && shouldPollTaskLogs(activeLogTaskId)
                  "
                  class="h-2 w-2 rounded-full bg-emerald-500"
                  title="日志自动刷新中"
                />
              </div>
              <div class="break-all text-xs text-gray-500">
                task_id: {{ activeLogTaskId || "-" }}
              </div>
              <div
                v-if="activeLogTask?.manus_task_id"
                class="flex flex-wrap items-center gap-2 text-xs text-amber-300"
              >
                <span class="min-w-0 break-all">
                  manus_task_id: {{ activeLogTask.manus_task_id }}
                </span>
                <UButton
                  size="xs"
                  color="primary"
                  variant="soft"
                  icon="material-symbols-light:open-in-new-rounded"
                  :to="manusAppUrl(activeLogTask.manus_task_id)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Manus
                </UButton>
              </div>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
              <UTabs
                v-model="logModalView"
                :items="logModalViewItems"
                value-key="value"
                :content="false"
                size="sm"
                color="primary"
                variant="pill"
              />
              <UButton
                size="sm"
                color="neutral"
                variant="soft"
                icon="material-symbols-light:refresh-rounded"
                :loading="logFetchLoading[activeLogTaskId]"
                :disabled="!activeLogTaskId"
                @click="refreshActiveLogContent"
              >
                刷新日志
              </UButton>
              <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                @click="closeLogModal"
              >
                关闭
              </UButton>
            </div>
          </div>

          <div class="mt-4 grid gap-2 text-xs text-gray-400 md:grid-cols-3">
            <div>
              创建时间 {{ formatDateTime(activeLogTask?.created_at) || "-" }}
            </div>
            <div>
              开始时间 {{ formatDateTime(activeLogTask?.started_at) || "-" }}
            </div>
            <div>
              结束时间 {{ formatDateTime(activeLogTask?.finished_at) || "-" }}
            </div>
            <div class="break-all">
              batch_id: {{ activeLogTask?.batch_id || "-" }}
            </div>
            <div v-if="shouldShowCurrentStep(activeLogTask)">
              当前步骤 {{ stepLabel(activeLogTask.current_step) }}
            </div>
            <div>
              日志大小 {{ formatFileSize(activeLogState.file_size || 0) }}
            </div>
          </div>

          <div
            v-if="activeLogTask?.error_message"
            class="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200 whitespace-pre-wrap break-all"
          >
            {{ activeLogTask.error_message }}
          </div>

          <div
            v-if="logModalView === 'parsed' && logDetailLoading"
            class="mt-4 flex min-h-[360px] items-center justify-center rounded-lg border border-gray-700 bg-black/20 text-sm text-gray-300"
          >
            <div class="flex items-center gap-2">
              <UIcon
                name="i-lucide-loader-2"
                class="h-4 w-4 animate-spin text-gray-400"
              />
              <span>加载日志详情...</span>
            </div>
          </div>

          <div
            v-else-if="logModalView === 'parsed'"
            class="mt-4 grid max-h-[calc(85vh-210px)] gap-4 overflow-y-auto pr-1 custom-scroll lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]"
          >
            <div class="min-w-0 space-y-4">
              <div class="rounded-lg border border-gray-700 bg-black/20 p-3">
                <div class="mb-3 flex items-center justify-between gap-2">
                  <div class="text-xs text-gray-500">
                    {{ completedTimelineCount }}/{{ activeLogTimeline.length }}
                  </div>
                </div>
                <div
                  class="max-h-[420px] space-y-2 overflow-y-auto pr-1 custom-scroll"
                >
                  <button
                    v-for="step in activeLogTimeline"
                    :key="`log-step-${step.key}`"
                    type="button"
                    class="flex w-full items-start gap-3 rounded-lg border p-2.5 text-left transition-colors"
                    :class="[
                      timelineStepClass(step.state),
                      activeLogSelectedStepKey === step.key
                        ? 'ring-1 ring-primary/70'
                        : '',
                    ]"
                    @click="activeLogSelectedStepKey = step.key"
                  >
                    <span
                      class="mt-1 h-2 w-2 shrink-0 rounded-full"
                      :class="timelineDotClass(step.state)"
                    />
                    <span class="min-w-0 flex-1">
                      <span class="flex flex-wrap items-center gap-2">
                        <span class="text-xs font-medium text-gray-100">{{
                          step.label
                        }}</span>
                        <UBadge
                          size="xs"
                          :color="timelineBadgeColor(step.state)"
                          variant="subtle"
                        >
                          {{ timelineStateLabel(step.state) }}
                        </UBadge>
                        <span
                          v-if="step.triggerLabel"
                          class="text-[11px] text-gray-500"
                        >
                          {{ step.triggerLabel }}
                        </span>
                      </span>
                      <span
                        class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500"
                      >
                        <span v-if="step.started_at"
                          >开始 {{ formatDateTime(step.started_at) }}</span
                        >
                        <span v-if="step.finished_at"
                          >结束 {{ formatDateTime(step.finished_at) }}</span
                        >
                        <span v-if="step.durationLabel"
                          >耗时 {{ step.durationLabel }}</span
                        >
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div
              class="min-w-0 overflow-hidden rounded-lg border border-gray-700 bg-black/20 p-3"
            >
              <div
                class="mb-3 flex flex-wrap items-center justify-between gap-2"
              >
                <div class="text-sm font-medium text-white">
                  {{ activeLogSelectedStep?.label || "步骤详情" }}
                </div>
                <UBadge
                  v-if="activeLogSelectedStep"
                  size="xs"
                  :color="timelineBadgeColor(activeLogSelectedStep.state)"
                  variant="subtle"
                >
                  {{ timelineStateLabel(activeLogSelectedStep.state) }}
                </UBadge>
              </div>
              <div
                v-if="activeLogSelectedStep"
                class="max-h-[calc(85vh-280px)] space-y-3 overflow-y-auto pr-1 custom-scroll"
              >
                <div
                  v-if="activeLogSelectedStep.error_message"
                  class="rounded-md border border-red-500/30 bg-red-500/5 p-2 text-xs text-red-200 whitespace-pre-wrap break-all"
                >
                  {{ activeLogSelectedStep.error_message }}
                </div>
                <div class="space-y-1">
                  <div class="text-xs text-gray-400">输入参数</div>
                  <pre
                    class="max-h-[260px] overflow-auto rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs text-gray-200 whitespace-pre-wrap wrap-break-word custom-scroll"
                    >{{
                      formatStepPayload(
                        activeLogSelectedStep.input_payload,
                        "暂无输入参数"
                      )
                    }}</pre
                  >
                </div>
                <div class="space-y-1">
                  <div class="text-xs text-gray-400">输出产物</div>
                  <pre
                    class="max-h-[260px] overflow-auto rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs text-gray-200 whitespace-pre-wrap wrap-break-word custom-scroll"
                    >{{
                      formatStepPayload(
                        activeLogSelectedStep.output_payload,
                        "暂无输出产物"
                      )
                    }}</pre
                  >
                </div>
              </div>
              <div v-else class="text-xs text-gray-500">暂无步骤详情</div>
            </div>
          </div>

          <div
            v-else
            class="mt-4 rounded-lg border border-gray-700 bg-gray-950/80 p-3"
          >
            <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div class="text-xs text-gray-400">日志原文</div>
              <div
                class="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500"
              >
                <span
                  >创建
                  {{ formatDateTime(activeLogState.created_at) || "-" }}</span
                >
                <span
                  >更新
                  {{ formatDateTime(activeLogState.updated_at) || "-" }}</span
                >
                <span
                  >刷新
                  {{ formatDateTime(activeLogState.fetched_at) || "-" }}</span
                >
                <span>offset {{ activeLogState.next_offset || 0 }}</span>
                <span
                  >size
                  {{ formatFileSize(activeLogState.file_size || 0) }}</span
                >
              </div>
            </div>
            <pre
              :ref="(el) => setLogContentRef(activeLogTaskId, el)"
              class="max-h-[620px] overflow-y-auto whitespace-pre-wrap wrap-break-word text-xs text-gray-200 custom-scroll"
              >{{ activeLogState.content || "暂无日志" }}</pre
            >
          </div>
        </UCard>
      </template>
    </UModal>

    <UModal v-model:open="assetDetailOpen" :ui="{ content: 'max-w-3xl' }">
      <template #content>
        <UCard class="bg-gray-900 ring-1 ring-gray-700" :ui="{ body: 'p-6' }">
          <div class="flex items-start justify-between gap-3">
            <div>
              <div class="text-base font-semibold text-white">
                {{ assetDetail.label || "产物详情" }}
              </div>
              <a
                v-if="assetDetail.url"
                :href="assetDetail.url"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-1 block break-all text-xs text-cyan-300 hover:text-primary"
              >
                {{ assetDetail.url }}
              </a>
              <label
                v-if="assetDetail.manusSelectable && assetDetail.source"
                class="mt-3 inline-flex items-center gap-2 text-xs text-gray-300"
              >
                <UCheckbox
                  :model-value="
                    assetDetail.source.manus_video_source ===
                    assetDetail.manusVideoSource
                  "
                  :disabled="
                    Boolean(savingManusVideoSourceMap[assetDetail.source.id])
                  "
                  @update:model-value="
                    setSourceManusVideoSource(
                      assetDetail.source,
                      assetDetail.manusVideoSource,
                      $event
                    )
                  "
                />
                <span>发送给 Manus</span>
              </label>
            </div>
            <UButton
              color="neutral"
              variant="ghost"
              size="sm"
              @click="assetDetailOpen = false"
            >
              关闭
            </UButton>
          </div>
          <div class="mt-4 grid gap-4 md:grid-cols-2">
            <div
              class="overflow-hidden rounded-lg border border-gray-700 bg-black/30"
            >
              <video
                v-if="assetDetail.isVideo"
                :src="assetDetail.url"
                :key="assetDetail.url"
                controls
                playsinline
                class="max-h-[420px] w-full object-contain"
              />
              <img
                v-else
                :src="assetDetail.url"
                alt=""
                class="max-h-[420px] w-full object-contain"
              />
            </div>
            <div class="space-y-2">
              <div class="text-sm font-medium text-white">输入参数</div>
              <pre
                class="wrap-break-word whitespace-pre-wrap max-h-[420px] overflow-auto rounded-lg border border-gray-700 bg-gray-950 p-3 text-xs text-gray-200 custom-scroll"
                >{{ formatJson(assetDetail.inputPayload) }}</pre
              >
            </div>
          </div>
        </UCard>
      </template>
    </UModal>

    <UModal v-model:open="workflowDagOpen" :ui="{ content: 'max-w-5xl' }">
      <template #content>
        <UCard class="bg-gray-900 ring-1 ring-gray-700" :ui="{ body: 'p-6' }">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div class="text-base font-semibold text-white">
                增量执行流程图
              </div>
              <div class="mt-1 text-xs text-gray-400">
                可配置节点 {{ activeWorkflowNodeCount }}/7 ·
                蓝框节点可点击，条纹节点为固定流程
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <UButton
                size="sm"
                color="primary"
                variant="soft"
                @click="setWorkflowNodes(true)"
              >
                全部开启
              </UButton>
              <UButton
                size="sm"
                color="neutral"
                variant="soft"
                @click="setWorkflowNodes(false)"
              >
                全部重置
              </UButton>
              <UButton
                size="sm"
                color="neutral"
                variant="ghost"
                @click="workflowDagOpen = false"
              >
                关闭
              </UButton>
            </div>
          </div>

          <div
            ref="workflowMermaidContainer"
            class="workflow-mermaid-container mt-4 overflow-auto rounded-lg border border-gray-700 bg-white p-4"
          >
            <svg style="width: 0; height: 0; position: absolute">
              <defs>
                <pattern
                  id="workflow-stripe-pattern"
                  patternUnits="userSpaceOnUse"
                  width="10"
                  height="10"
                  patternTransform="rotate(45)"
                >
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="10"
                    style="stroke: #cbd5e1; stroke-width: 10"
                  />
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="10"
                    style="stroke: #f1f5f9; stroke-width: 5"
                  />
                </pattern>
              </defs>
            </svg>
            <div ref="workflowMermaidRoot" class="flex justify-center">
              <div class="text-sm text-slate-500">流程图加载中...</div>
            </div>
          </div>
        </UCard>
      </template>
    </UModal>

    <UModal v-model:open="categoryModalOpen" :ui="{ content: 'max-w-2xl' }">
      <template #content>
        <UCard
          class="bg-gray-900 ring-1 ring-gray-700"
          :ui="{ body: 'p-6 space-y-4' }"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <div class="text-base font-semibold text-white">分类管理</div>
              <div class="mt-1 text-xs text-gray-400">
                分组：{{ WORKFLOW_EFFECTS_CATEGORY_GROUP }} ·
                新增后可在任务表单中选择
              </div>
            </div>
            <UButton
              color="neutral"
              variant="ghost"
              @click="categoryModalOpen = false"
            >
              关闭
            </UButton>
          </div>

          <div
            class="rounded-lg border border-gray-700 bg-black/20 p-3 space-y-3"
          >
            <div class="text-sm font-medium text-white">新增分类</div>
            <div
              class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
            >
              <label class="flex min-w-0 flex-col gap-1 text-xs text-gray-400">
                <span>分类名称</span>
                <UInput
                  v-model="newCategoryName"
                  placeholder="例如 Action Moves"
                  :disabled="categoryCreating"
                  @keyup.enter="createWorkflowCategory"
                />
              </label>
              <label class="flex min-w-0 flex-col gap-1 text-xs text-gray-400">
                <span>分类 ID</span>
                <UInput
                  v-model="newCategoryId"
                  placeholder="根据名称自动生成"
                  :disabled="categoryCreating"
                  @input="onNewCategoryIdInput"
                  @keyup.enter="createWorkflowCategory"
                />
              </label>
              <div class="flex items-end">
                <UButton
                  icon="material-symbols-light:add-circle-outline-rounded"
                  :loading="categoryCreating"
                  :disabled="categoryCreating"
                  @click="createWorkflowCategory"
                >
                  添加
                </UButton>
              </div>
            </div>
            <div v-if="categoryFormError" class="text-sm text-red-400">
              {{ categoryFormError }}
            </div>
          </div>

          <div class="space-y-2">
            <div class="flex items-center justify-between gap-2">
              <div class="text-sm font-medium text-white">当前分类</div>
              <div class="flex items-center gap-2">
                <div v-if="loadingCategories" class="text-xs text-gray-500">
                  正在加载...
                </div>
                <template v-if="canShowPublish">
                  <UButton
                    v-if="!categoryBatchMode"
                    size="xs"
                    color="success"
                    variant="solid"
                    icon="lucide:list-checks"
                    :disabled="!!publishingKey"
                    @click="startCategoryBatchPublish"
                  >
                    {{
                      getPublishProgressText(
                        `${CATEGORIES_PUBLISH_RESOURCE}:workflow-batch`
                      ) || "分类批量上线"
                    }}
                  </UButton>
                  <div
                    v-else
                    class="flex items-center gap-2 rounded-md border border-gray-700 px-2 py-1"
                  >
                    <span class="text-xs text-gray-400"
                      >已选 {{ selectedCategoryPublishCount }} 项</span
                    >
                    <UButton
                      size="xs"
                      color="neutral"
                      variant="soft"
                      @click="selectAllPublishableCategories"
                    >
                      全选
                    </UButton>
                    <UButton
                      size="xs"
                      color="neutral"
                      variant="soft"
                      @click="cancelCategoryBatchPublish"
                    >
                      取消
                    </UButton>
                    <UButton
                      size="xs"
                      color="success"
                      variant="solid"
                      :loading="
                        publishingKey ===
                        `${CATEGORIES_PUBLISH_RESOURCE}:workflow-batch`
                      "
                      :disabled="
                        !selectedCategoryPublishCount || !!publishingKey
                      "
                      @click="confirmCategoryBatchPublish"
                    >
                      确认
                    </UButton>
                  </div>
                </template>
              </div>
            </div>
            <div
              v-if="!loadingCategories && !categories.length"
              class="rounded-lg border border-dashed border-gray-700 px-4 py-6 text-center text-sm text-gray-500"
            >
              暂无分类，请先新增
            </div>
            <div
              v-else
              class="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2"
            >
              <div
                v-for="category in categories"
                :key="category.id || category.templateCategory"
                class="rounded-lg border border-gray-700 bg-black/20 px-3 py-2"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <div class="truncate text-sm text-white">
                      {{ category.categoryName || "未命名分类" }}
                    </div>
                    <div class="mt-1 text-xs text-gray-500">
                      ID: {{ category.templateCategory }}
                    </div>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    <UCheckbox
                      v-if="categoryBatchMode"
                      size="xs"
                      :model-value="isCategoryPublishSelected(category)"
                      :disabled="!canPublishCategory(category)"
                      @update:model-value="
                        (checked) =>
                          toggleCategoryPublishSelection(category, checked)
                      "
                    />
                    <UButton
                      v-if="canShowPublish && !categoryBatchMode"
                      icon="lucide:cloud-upload"
                      size="xs"
                      variant="solid"
                      :color="getCategoryPublishButtonState(category).color"
                      :loading="
                        publishingKey ===
                        `${CATEGORIES_PUBLISH_RESOURCE}:workflow:${category.id}`
                      "
                      :disabled="
                        getCategoryPublishButtonState(category).disabled
                      "
                      :title="getCategoryPublishButtonState(category).title"
                      @click="publishCategory(category)"
                    >
                      {{ getCategoryPublishButtonState(category).label }}
                    </UButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </template>
    </UModal>

    <UModal
      v-model:open="confirmModalOpen"
      :dismissible="!confirmModalLoading"
      :ui="{ content: 'max-w-md' }"
      @update:open="onConfirmModalUpdateOpen"
    >
      <template #content>
        <UCard class="bg-gray-900 ring-1 ring-gray-700" :ui="{ body: 'p-6' }">
          <div class="text-base font-semibold text-white">请确认</div>
          <div class="mt-2 text-sm whitespace-pre-wrap text-gray-300">
            {{ confirmModalMessage }}
          </div>
          <div class="mt-6 flex justify-end gap-2">
            <UButton
              color="neutral"
              variant="ghost"
              :disabled="confirmModalLoading"
              @click="closeConfirmModal"
            >
              取消
            </UButton>
            <UButton
              color="primary"
              class="text-white"
              :loading="confirmModalLoading"
              @click="submitConfirmModal"
            >
              确认
            </UButton>
          </div>
        </UCard>
      </template>
    </UModal>
  </section>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import WorkflowAdminService from "@/services/workflow-admin-service";

const EFFECTS_CONFIG_API_PREFIX = "sharkfoto/admin/effects-configs";
const CATEGORIES_API_PREFIX = "sharkfoto/admin/effects-categories";
/** 工作流分类固定分组；分类列表在前端筛该分组 */
const WORKFLOW_EFFECTS_CATEGORY_GROUP = "video";
const EFFECTS_PUBLISH_RESOURCE = "effects-configs";
const CATEGORIES_PUBLISH_RESOURCE = "effects-categories";
const SOURCE_SUCCESS_STATUSES = new Set(["success", "succeeded"]);
const MANUS_VIDEO_SOURCE_COVER = "source_cover";
const MANUS_VIDEO_SOURCE_VIDEO = "source_video";

const {
  publishingKey,
  canShowPublish,
  publishStatusQuery,
  getPublishButtonState,
  getPublishProgressText,
  publishItem,
  publishItems,
} = useAdminPublish();
const { $axios } = useNuxtApp();
const runtimeConfig = useRuntimeConfig();
const toast = useToast();

function normalizeText(value) {
  return value == null ? "" : String(value).trim();
}

function normalizeLowerText(value) {
  return normalizeText(value).toLowerCase();
}

function effectConfigCode(item) {
  return normalizeText(item?.code ?? item?.payload?.code);
}

function effectConfigType(item) {
  return normalizeLowerText(
    item?.effect_type ?? item?.payload?.effect_type ?? "image"
  );
}

function isLikelyVideoUrl(url) {
  if (!url || typeof url !== "string") {
    return false;
  }
  const path = url.split("?")[0].toLowerCase();
  return /\.(mp4|webm|mpe?g|mov|mkv|m4v)$/.test(path);
}

function sourceBaseInputPayload(item) {
  return {
    effect_title: item.effect_title,
    template_code: item.template_code,
    source_video_url: item.source_video_url,
    source_cover_url: item.source_cover_url,
    manus_video_source: item.manus_video_source,
    category_name: item.category_name,
    template_category: item.template_category,
    assets_num: item.assets_num,
    notes: item.notes,
    updated_at: item.updated_at,
  };
}

function stepInputPayload(latestAsset, stepKey) {
  return latestAsset?.step_summaries?.[stepKey]?.input_payload || null;
}

/** 任务源卡片：展示原始资源和关键生成步骤产物，点击查看输入参数 */
function sourcePreviewTiles(item) {
  if (!item) {
    return [];
  }
  const tiles = [];
  const push = (key, label, url, isVideo, inputPayload, options = {}) => {
    const u = url != null ? String(url).trim() : "";
    if (!u) {
      return;
    }
    tiles.push({
      key,
      label,
      url: u,
      isVideo: typeof isVideo === "boolean" ? isVideo : isLikelyVideoUrl(u),
      inputPayload: inputPayload || null,
      manusVideoSource: options.manusVideoSource || "",
      manusSelectable: Boolean(options.manusVideoSource),
    });
  };
  const raw = item.raw_payload;
  const coverVideo =
    raw && typeof raw === "object" && raw.coverVideo != null
      ? String(raw.coverVideo).trim()
      : "";
  const la = item.latest_asset;
  push(
    "source-cover",
    "源封面",
    item.source_cover_url || la?.old_cover_url || coverVideo,
    true,
    sourceBaseInputPayload(item),
    {
      manusVideoSource: normalizeText(item.source_cover_url)
        ? MANUS_VIDEO_SOURCE_COVER
        : "",
    }
  );
  push(
    "src-video",
    "原作品",
    item.source_video_url,
    true,
    sourceBaseInputPayload(item),
    {
      manusVideoSource: normalizeText(item.source_video_url)
        ? MANUS_VIDEO_SOURCE_VIDEO
        : "",
    }
  );
  if (la) {
    push(
      "mock-image",
      "新文生图",
      la.mock_user_image_url,
      false,
      stepInputPayload(la, "image")
    );
    push(
      "first-frame",
      "新首帧",
      la.first_frame_url,
      false,
      stepInputPayload(la, "first_frame")
    );
    push(
      "tail-frame",
      "新尾帧",
      la.tail_frame_url,
      false,
      stepInputPayload(la, "tail_frame")
    );
    push(
      "out-video",
      "成品视频",
      la.compressed_video_url,
      true,
      stepInputPayload(la, "video")
    );
  }
  return tiles;
}

const sourcePreviewTilesMap = computed(() => {
  const out = {};
  for (const item of pagedSources.value) {
    out[item.id] = sourcePreviewTiles(item);
  }
  return out;
});

const processKindItems = [
  { label: "新会话全量生成", value: "manus_new_session" },
  { label: "已有会话增量执行", value: "manus_update" },
];

const workflowStepDefinitions = [
  { key: "old_cover", label: "原视频首帧", shortLabel: "原首帧" },
  { key: "old_video_webp", label: "原视频动态 WebP", shortLabel: "原 WebP" },
  {
    key: "source_cover_webp",
    label: "源封面视频动态 WebP",
    shortLabel: "源封面",
  },
  { key: "manus", label: "Manus 处理", shortLabel: "Manus" },
  {
    key: "workflow_config",
    label: "构建 workflow 配置",
    shortLabel: "workflow",
  },
  { key: "page_config", label: "构建 page_config", shortLabel: "page" },
  { key: "bgm", label: "BGM process", shortLabel: "BGM" },
  { key: "image", label: "文生图", shortLabel: "文生图" },
  { key: "first_frame", label: "首帧生成", shortLabel: "首帧" },
  { key: "tail_frame", label: "尾帧生成", shortLabel: "尾帧" },
  { key: "video", label: "图生视频", shortLabel: "视频" },
  { key: "new_video_webp", label: "新视频动态 WebP", shortLabel: "新 WebP" },
  { key: "merge_bgm", label: "合并 BGM", shortLabel: "合 BGM" },
  { key: "compress_video", label: "压缩视频", shortLabel: "压缩" },
  { key: "sync_effect", label: "入库 effects_config", shortLabel: "入库" },
  { key: "update_api", label: "调用更新接口", shortLabel: "更新接口" },
  { key: "notify", label: "发送飞书通知", shortLabel: "通知" },
];

const workflowStepDefinitionMap = workflowStepDefinitions.reduce(
  (out, step) => {
    out[step.key] = step;
    return out;
  },
  {}
);

const workflowStepIndexMap = workflowStepDefinitions.reduce(
  (out, step, index) => {
    out[step.key] = index;
    return out;
  },
  {}
);

const logModalViewItems = [
  { label: "解析视图", value: "parsed" },
  { label: "原文 txt", value: "raw" },
];

const workflowClickableNodeIds = ["C", "E", "F", "G", "H", "I", "J"];
const workflowNodeFlagMap = {
  C: "manus_enabled",
  E: "html_enabled",
  F: "bgm_enabled",
  G: "image_enabled",
  H: "first_frame_enabled",
  I: "tail_frame_enabled",
  J: "video_enabled",
};
const workflowMermaidSource = `
flowchart TD
    A["开始任务"] --> C["Manus 处理结果"]
    C --> D["构建 workflow_config"]
    C --> E["构建 page_config / HTML"]

    D --> F["处理 BGM"]
    D --> G["文生图"]

    G --> H["首帧生成"]
    G --> I["尾帧生成"]

    H --> J["图生视频"]
    I --> J
    G --> J

    F --> K["合并 BGM"]
    J --> K

    K --> M["压缩视频"]
    J --> M

    D --> N["入库/通知"]
    E --> N
    M --> N

    class A node-start;
    class D,K,M node-readonly;
    class N node-notice;

    class C,E,F,G,H,I,J node-clickable;

    click C call toggleNodeStatus()
    click E call toggleNodeStatus()
    click F call toggleNodeStatus()
    click G call toggleNodeStatus()
    click H call toggleNodeStatus()
    click I call toggleNodeStatus()
    click J call toggleNodeStatus()
`;

const statusColorMap = {
  queued: "neutral",
  running: "primary",
  success: "success",
  succeeded: "success",
  failed: "error",
  cancelled: "warning",
  skipped: "warning",
};

const loadingAll = ref(false);
const loadingSources = ref(true);
const loadingBatches = ref(true);
const loadingRunTasks = ref(true);
const savingSource = ref(false);
const deletingSourceId = ref("");
const creatingBatch = ref(false);
const cancellingBatchId = ref("");
const purgingBatchId = ref("");
const batchPurging = ref(false);
const selectedBatchIds = ref([]);
const cancellingTaskId = ref("");

const keyword = ref("");
const processKind = ref("manus_new_session");
const sourceFormExpanded = ref(false);
const concurrencyLimit = ref(1);
const updatePrompt = ref("");
const partialFlags = reactive({
  manus_enabled: false,
  html_enabled: false,
  image_enabled: false,
  first_frame_enabled: false,
  tail_frame_enabled: false,
  video_enabled: false,
  bgm_enabled: false,
});
const selectedIds = ref([]);
const sources = ref([]);
const sourcePage = ref(1);
const sourceItemsPerPage = ref(10);
const sourceStatusFilter = ref("all");
const sourceCategoryFilter = ref("all");
const publishStatusFilter = ref("all");
const sourceNotesFilter = ref("all");
const batches = ref([]);
const runTasks = ref([]);
const effectsConfigs = ref([]);
const categories = ref([]);
const loadingEffectsConfigs = ref(false);
const loadingCategories = ref(false);
const effectsConfigsError = ref("");
const activeBatchId = ref("");
const runTaskBatchFilterDraft = ref("");
const logFetchLoading = reactive({});
const logDetailLoading = ref(false);
const logDetailLoaded = reactive({});
const savingManusVideoSourceMap = reactive({});
const sourceActionMessage = ref("");
const batchActionMessage = ref("");
const runTasksLoadedOnce = ref(false);

const assetDetailOpen = ref(false);
const workflowDagOpen = ref(false);
const logModalOpen = ref(false);
const logModalView = ref("parsed");
const activeLogTaskId = ref("");
const activeLogSelectedStepKey = ref("");
const assetDetail = reactive({
  label: "",
  url: "",
  isVideo: false,
  inputPayload: null,
  source: null,
  manusVideoSource: "",
  manusSelectable: false,
});

const confirmModalOpen = ref(false);
const confirmModalMessage = ref("");
const confirmModalLoading = ref(false);
const pendingConfirmAction = ref(null);

function openConfirmModal(message, action) {
  confirmModalMessage.value = message;
  pendingConfirmAction.value = action;
  confirmModalOpen.value = true;
}

function closeConfirmModal() {
  if (confirmModalLoading.value) {
    return;
  }
  confirmModalOpen.value = false;
  confirmModalMessage.value = "";
  pendingConfirmAction.value = null;
}

function onConfirmModalUpdateOpen(open) {
  if (!open && !confirmModalLoading.value) {
    confirmModalMessage.value = "";
    pendingConfirmAction.value = null;
  }
}

async function submitConfirmModal() {
  const action = pendingConfirmAction.value;
  if (!action) {
    closeConfirmModal();
    return;
  }
  confirmModalLoading.value = true;
  try {
    await action();
  } finally {
    confirmModalLoading.value = false;
    confirmModalOpen.value = false;
    confirmModalMessage.value = "";
    pendingConfirmAction.value = null;
  }
}

const form = reactive({
  id: "",
  effect_title: "",
  template_code: "",
  source_video_url: "",
  source_cover_url: "",
  manus_video_source: "",
  category_name: "",
  template_category: "",
  assets_num: 1,
  notes: "",
});

const categoryModalOpen = ref(false);
const categoryCreating = ref(false);
const categoryFormError = ref("");
const newCategoryId = ref("");
const newCategoryName = ref("");
const newCategoryIdTouched = ref(false);
const categoryBatchMode = ref(false);
const selectedCategoryPublishKeys = ref(new Set());

function normalizeCsvValues(value) {
  const rawItems = Array.isArray(value)
    ? value
    : String(value || "").split(",");
  const normalized = [];
  const seen = new Set();
  rawItems.forEach((item) => {
    const text = normalizeText(item);
    if (!text || seen.has(text)) {
      return;
    }
    seen.add(text);
    normalized.push(text);
  });
  return normalized;
}

function buildCategorySlug(value) {
  const text = normalizeText(value);
  if (!text) {
    return "";
  }
  return text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildTemplateCode(value) {
  const text = normalizeText(value);
  if (!text) {
    return "";
  }
  return text.toLowerCase().replace(/\s+/g, "-");
}

function resolveFormTemplateCode(formValue = form) {
  const manualCode = normalizeText(formValue.template_code);
  if (manualCode) {
    return manualCode;
  }
  return buildTemplateCode(formValue.effect_title);
}

function findDuplicateTemplateCodeSource(templateCode, excludeId = "") {
  const normalizedCode = normalizeLowerText(templateCode);
  if (!normalizedCode) {
    return null;
  }
  return (
    sources.value.find((item) => {
      if (excludeId && item.id === excludeId) {
        return false;
      }
      return normalizeLowerText(item.template_code) === normalizedCode;
    }) || null
  );
}

const resolvedFormTemplateCode = computed(() => resolveFormTemplateCode());

const duplicateTemplateCodeSource = computed(() => {
  if (form.id) {
    return null;
  }
  return findDuplicateTemplateCodeSource(resolvedFormTemplateCode.value);
});

const formTemplateCodeAutoGenerated = computed(() => {
  return (
    !form.id &&
    !normalizeText(form.template_code) &&
    Boolean(resolvedFormTemplateCode.value)
  );
});

function isValidCategorySlug(value) {
  return /^[a-z0-9-]+$/.test(value);
}

const selectedCategoryOption = computed({
  get() {
    return normalizeCsvValues(form.template_category);
  },
  set(value) {
    const selectedIds = normalizeCsvValues(value);
    form.template_category = selectedIds.join(",");
    form.category_name = selectedIds
      .map((item) => categoryOptionMap.value.get(item)?.categoryName || item)
      .join(",");
  },
});

function resetCategoryForm() {
  newCategoryName.value = "";
  newCategoryId.value = "";
  newCategoryIdTouched.value = false;
  categoryFormError.value = "";
}

function onNewCategoryIdInput() {
  newCategoryIdTouched.value = true;
}

function openCategoryModal() {
  resetCategoryForm();
  categoryModalOpen.value = true;
  loadCategories();
}

async function createWorkflowCategory() {
  const categoryName = normalizeText(newCategoryName.value);
  const categoryId = normalizeText(newCategoryId.value);

  if (!categoryName || !categoryId) {
    categoryFormError.value = "请填写分类名称和分类 ID";
    return;
  }
  if (!isValidCategorySlug(categoryId)) {
    categoryFormError.value = "分类 ID 仅支持英文小写、数字和 -";
    return;
  }
  if (categories.value.some((item) => item.templateCategory === categoryId)) {
    categoryFormError.value = `分类 ID「${categoryId}」已存在，请更换名称或手动修改 ID`;
    return;
  }

  categoryCreating.value = true;
  categoryFormError.value = "";
  try {
    await $axios({
      method: "post",
      url: CATEGORIES_API_PREFIX,
      baseURL: runtimeConfig.public.apiBase,
      data: {
        category_id: categoryId,
        category_name: categoryName,
        group_name: WORKFLOW_EFFECTS_CATEGORY_GROUP,
      },
    });
    toast.add({
      title: "新增成功",
      description: `分类「${categoryName}」已创建`,
      color: "success",
    });
    resetCategoryForm();
    await loadCategories();
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "新增分类失败";
    categoryFormError.value = message;
    toast.add({
      title: "新增失败",
      description: message,
      color: "error",
    });
  } finally {
    categoryCreating.value = false;
  }
}

function categoryPublishKey(category) {
  return String(category?.id || category?.templateCategory || "");
}

function isCategoryInvalid(category) {
  return (
    !normalizeText(category?.id) ||
    !normalizeText(category?.templateCategory) ||
    !normalizeText(category?.categoryName)
  );
}

function getCategoryPublishButtonState(category) {
  return getPublishButtonState(category, {
    isBusy: Boolean(publishingKey.value),
    isInvalid: isCategoryInvalid(category),
    fallbackLabel: "上线",
  });
}

function canPublishCategory(category) {
  if (!canShowPublish.value) {
    return false;
  }
  if (!category?.id) {
    return false;
  }
  return !getCategoryPublishButtonState(category).disabled;
}

const selectedCategoryPublishRows = computed(() =>
  categories.value.filter(
    (category) =>
      selectedCategoryPublishKeys.value.has(categoryPublishKey(category)) &&
      canPublishCategory(category)
  )
);

const selectedCategoryPublishCount = computed(
  () => selectedCategoryPublishRows.value.length
);

function startCategoryBatchPublish() {
  selectedCategoryPublishKeys.value = new Set();
  categoryBatchMode.value = true;
}

function cancelCategoryBatchPublish() {
  selectedCategoryPublishKeys.value = new Set();
  categoryBatchMode.value = false;
}

function isCategoryPublishSelected(category) {
  return selectedCategoryPublishKeys.value.has(categoryPublishKey(category));
}

function toggleCategoryPublishSelection(category, checked) {
  const key = categoryPublishKey(category);
  if (!key) {
    return;
  }
  const next = new Set(selectedCategoryPublishKeys.value);
  if (checked) {
    if (canPublishCategory(category)) {
      next.add(key);
    }
  } else {
    next.delete(key);
  }
  selectedCategoryPublishKeys.value = next;
}

function selectAllPublishableCategories() {
  selectedCategoryPublishKeys.value = new Set(
    categories.value
      .filter((category) => canPublishCategory(category))
      .map((category) => categoryPublishKey(category))
      .filter(Boolean)
  );
}

async function publishCategory(category) {
  if (!category?.id) {
    toast.add({
      title: "无法上线",
      description: "请先保存到测试环境，生成唯一 ID 后再上线",
      color: "warning",
    });
    return;
  }
  const result = await publishItem(
    CATEGORIES_PUBLISH_RESOURCE,
    category.id,
    `${CATEGORIES_PUBLISH_RESOURCE}:workflow:${category.id}`
  );
  if (result) {
    await loadCategories();
  }
}

async function confirmCategoryBatchPublish() {
  const ids = selectedCategoryPublishRows.value
    .map((category) => normalizeText(category?.id))
    .filter(Boolean);
  if (!ids.length) {
    toast.add({
      title: "无法批量上线",
      description: "请先选择可上线的分类",
      color: "warning",
    });
    return;
  }
  const result = await publishItems(
    CATEGORIES_PUBLISH_RESOURCE,
    ids,
    `${CATEGORIES_PUBLISH_RESOURCE}:workflow-batch`,
    {
      confirmMessage: `确认上线选中的 ${ids.length} 个分类到生产环境？`,
    }
  );
  if (result) {
    cancelCategoryBatchPublish();
    await loadCategories();
  }
}

const logStates = reactive({});
const logContentRefs = reactive({});
const sourceListRef = ref(null);
const workflowMermaidContainer = ref(null);
const workflowMermaidRoot = ref(null);

let taskPollTimer = null;
let batchPollTimer = null;
let logPollTimer = null;
let mermaidLoadPromise = null;

const sourceKeywordFilteredSources = computed(() => {
  const search = keyword.value.trim().toLowerCase();
  if (!search) {
    return sources.value;
  }
  return sources.value.filter((item) => {
    return [
      item.effect_title,
      item.template_code,
      item.category_name,
      item.template_category,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(search));
  });
});

function matchesSourceFilters(item, skip = {}) {
  if (!skip.status && sourceStatusFilter.value !== "all") {
    const status = sourceDisplayStatus(item) || "no_status";
    if (status !== sourceStatusFilter.value) {
      return false;
    }
  }
  if (!skip.category && sourceCategoryFilter.value !== "all") {
    const categories = normalizeCsvValues(item?.template_category);
    if (!categories.length) {
      if (sourceCategoryFilter.value !== "uncategorized") {
        return false;
      }
    } else if (!categories.includes(sourceCategoryFilter.value)) {
      return false;
    }
  }
  if (
    !skip.publish &&
    canShowPublish.value &&
    publishStatusFilter.value !== "all"
  ) {
    if (sourcePublishMeta(item).state !== publishStatusFilter.value) {
      return false;
    }
  }
  if (!skip.notes && sourceNotesFilter.value !== "all") {
    const notes = normalizeText(item?.notes);
    if (sourceNotesFilter.value === "empty") {
      if (notes) {
        return false;
      }
    } else if (notes !== sourceNotesFilter.value) {
      return false;
    }
  }
  return true;
}

function filterSourcesWithCascade(list, skip = {}) {
  return list.filter((item) => matchesSourceFilters(item, skip));
}

function resetSourceFilters() {
  sourceStatusFilter.value = "all";
  sourceCategoryFilter.value = "all";
  publishStatusFilter.value = "all";
  sourceNotesFilter.value = "all";
  sourcePage.value = 1;
  nextTick(scrollSourceListToTop);
}

const sourceStatusItems = computed(() => {
  const base = filterSourcesWithCascade(sourceKeywordFilteredSources.value, {
    status: true,
  });
  const counts = new Map();
  for (const item of base) {
    const status = sourceDisplayStatus(item) || "no_status";
    counts.set(status, (counts.get(status) || 0) + 1);
  }
  const items = [
    {
      label: `全部 ${base.length}`,
      value: "all",
    },
  ];
  for (const [status, count] of counts) {
    items.push({
      label: `${status === "no_status" ? "无状态" : status} ${count}`,
      value: status,
    });
  }
  return items;
});

const categorySelectItems = computed(() => {
  return categories.value.map((item) => ({
    label: `${item.categoryName || "未命名分类"} / ${item.templateCategory}`,
    value: item.templateCategory,
  }));
});

const categoryOptionMap = computed(() => {
  const map = new Map();
  for (const item of categories.value) {
    if (!item.templateCategory || map.has(item.templateCategory)) {
      continue;
    }
    map.set(item.templateCategory, item);
  }
  return map;
});

const sourceCategoryOptions = computed(() => {
  const map = new Map();
  for (const item of categories.value) {
    const templateCategory = normalizeText(item?.templateCategory);
    const categoryName = normalizeText(item?.categoryName);
    if (!templateCategory) {
      continue;
    }
    if (!map.has(templateCategory)) {
      map.set(templateCategory, {
        templateCategory,
        categoryName: categoryName || templateCategory,
      });
    }
  }
  for (const item of sources.value) {
    const templateCategories = normalizeCsvValues(item?.template_category);
    const categoryNames = normalizeCsvValues(item?.category_name);
    templateCategories.forEach((templateCategory, index) => {
      if (!templateCategory || map.has(templateCategory)) {
        return;
      }
      map.set(templateCategory, {
        templateCategory,
        categoryName: categoryNames[index] || templateCategory,
      });
    });
  }
  return Array.from(map.values()).sort((a, b) => {
    return a.templateCategory.localeCompare(b.templateCategory, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
});

const sourceCategoryItems = computed(() => {
  const base = filterSourcesWithCascade(sourceKeywordFilteredSources.value, {
    category: true,
  });
  const counts = new Map();
  for (const item of base) {
    const templateCategories = normalizeCsvValues(item?.template_category);
    if (!templateCategories.length) {
      counts.set("uncategorized", (counts.get("uncategorized") || 0) + 1);
      continue;
    }
    templateCategories.forEach((category) => {
      counts.set(category, (counts.get(category) || 0) + 1);
    });
  }
  const items = [
    {
      label: `全部分类 ${base.length}`,
      value: "all",
    },
  ];
  for (const option of sourceCategoryOptions.value) {
    const count = counts.get(option.templateCategory) || 0;
    counts.delete(option.templateCategory);
    if (count <= 0) {
      continue;
    }
    items.push({
      label: `${option.categoryName} ${count}`,
      value: option.templateCategory,
    });
  }
  const uncategorizedCount = counts.get("uncategorized") || 0;
  if (uncategorizedCount > 0) {
    items.push({
      label: `未分类 ${uncategorizedCount}`,
      value: "uncategorized",
    });
  }
  return items;
});

const publishStatusFilterLabelMap = {
  publishable: "可上线",
  published: "已上线",
  not_synced: "未入库",
  not_success: "非 success",
  blocked: "状态异常",
};

const publishStatusItems = computed(() => {
  if (!canShowPublish.value) {
    return [];
  }
  const base = filterSourcesWithCascade(sourceKeywordFilteredSources.value, {
    publish: true,
  });
  const counts = new Map();
  for (const item of base) {
    const state = sourcePublishMeta(item).state;
    if (!state || state === "hidden") {
      continue;
    }
    counts.set(state, (counts.get(state) || 0) + 1);
  }
  const items = [
    {
      label: `全部上线状态 ${base.length}`,
      value: "all",
    },
  ];
  for (const [state, count] of counts) {
    items.push({
      label: `${publishStatusFilterLabelMap[state] || state} ${count}`,
      value: state,
    });
  }
  return items;
});

const formSourceOriginOptions = computed(() => {
  const values = new Set();
  for (const item of sources.value) {
    const notes = normalizeText(item?.notes);
    if (notes) {
      values.add(notes);
    }
  }
  return Array.from(values).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );
});

const formSourceOriginSelectItems = computed(() => {
  return formSourceOriginOptions.value.map((item) => ({
    label: item,
    value: item,
  }));
});

const selectedSourceOriginOption = computed({
  get() {
    const value = normalizeText(form.notes);
    if (!value) {
      return undefined;
    }
    return formSourceOriginOptions.value.includes(value) ? value : undefined;
  },
  set(value) {
    form.notes = normalizeText(value);
  },
});

const sourceNotesItems = computed(() => {
  const base = filterSourcesWithCascade(sourceKeywordFilteredSources.value, {
    notes: true,
  });
  const counts = new Map();
  let emptyCount = 0;
  for (const item of base) {
    const notes = normalizeText(item?.notes);
    if (!notes) {
      emptyCount += 1;
      continue;
    }
    counts.set(notes, (counts.get(notes) || 0) + 1);
  }
  const items = [
    {
      label: `全部来源 ${base.length}`,
      value: "all",
    },
  ];
  if (emptyCount) {
    items.push({
      label: `无来源 ${emptyCount}`,
      value: "empty",
    });
  }
  for (const notes of Array.from(counts.keys()).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  )) {
    const preview = notes.length > 40 ? `${notes.slice(0, 40)}…` : notes;
    items.push({
      label: `${preview} ${counts.get(notes)}`,
      value: notes,
    });
  }
  return items;
});

const filteredSources = computed(() => {
  return filterSourcesWithCascade(sourceKeywordFilteredSources.value);
});

const sourcePageCount = computed(() => {
  return Math.max(
    1,
    Math.ceil(filteredSources.value.length / sourceItemsPerPage.value)
  );
});

const showSourcePagination = computed(
  () => filteredSources.value.length > sourceItemsPerPage.value
);

const pagedSources = computed(() => {
  const start = (sourcePage.value - 1) * sourceItemsPerPage.value;
  return filteredSources.value.slice(start, start + sourceItemsPerPage.value);
});

/** 任务源列表仅 10 条/页：首屏外缩略图也用 preload=auto + 提前 decode，避免滚动时批量挂载/解码导致卡顿 */
function primeSourceListThumbnails() {
  const root = sourceListRef.value;
  if (!root?.querySelectorAll) {
    return;
  }
  for (const img of root.querySelectorAll("img")) {
    if (img.loading === "lazy") {
      img.loading = "eager";
    }
    const decodePromise =
      typeof img.decode === "function" ? img.decode() : null;
    if (decodePromise) {
      decodePromise.catch(() => {});
    }
  }
  for (const video of root.querySelectorAll("video")) {
    try {
      video.preload = "auto";
    } catch {
      /* ignore */
    }
  }
}

watch(
  pagedSources,
  () => {
    nextTick(() => primeSourceListThumbnails());
  },
  { flush: "post" }
);

const latestRunTaskBySourceId = computed(() => {
  const out = {};
  for (const task of runTasks.value || []) {
    const sourceId = task?.source_id;
    if (!sourceId || out[sourceId]) {
      continue;
    }
    out[sourceId] = task;
  }
  return out;
});

const videoEffectsConfigMap = computed(() => {
  const out = new Map();
  for (const item of effectsConfigs.value || []) {
    if (effectConfigType(item) !== "video") {
      continue;
    }
    const code = effectConfigCode(item);
    if (!code || out.has(code)) {
      continue;
    }
    out.set(code, item);
  }
  return out;
});

function sourceDisplayStatus(item) {
  return (
    latestRunTaskBySourceId.value[item?.id]?.status || item?.last_run_status
  );
}

function isSourceSuccessStatus(status) {
  return SOURCE_SUCCESS_STATUSES.has(normalizeLowerText(status));
}

function sourceTemplateCode(item) {
  return String(item?.template_code || "").trim();
}

function matchedVideoEffectsConfig(item) {
  const code = sourceTemplateCode(item);
  return code ? videoEffectsConfigMap.value.get(code) || null : null;
}

function buildSourcePublishMeta(item) {
  const runStatus = sourceDisplayStatus(item);
  const base = {
    state: "not_success",
    label: "非 success",
    color: "neutral",
    reason: "仅 success 状态允许上线",
    disabled: true,
    matchedConfig: null,
    matchedConfigId: "",
    publishStatus: null,
  };

  if (!canShowPublish.value) {
    return {
      ...base,
      state: "hidden",
      label: "上线",
      reason: "",
    };
  }

  if (effectsConfigsError.value) {
    return {
      ...base,
      state: "blocked",
      label: "状态加载失败",
      color: "warning",
      reason: effectsConfigsError.value,
    };
  }

  if (loadingEffectsConfigs.value) {
    return {
      ...base,
      state: "blocked",
      label: "状态加载中",
      reason: "正在加载 video effects-configs 状态",
    };
  }

  if (!isSourceSuccessStatus(runStatus)) {
    return base;
  }

  const matchedConfig = matchedVideoEffectsConfig(item);
  const matchedConfigId = normalizeText(matchedConfig?.id);
  if (!matchedConfigId) {
    return {
      ...base,
      state: "not_synced",
      label: "未入库",
      color: "warning",
      reason: "请先确认 success 入库已同步到 pop 配置",
    };
  }

  const publishButtonState = getPublishButtonState(matchedConfig, {
    isBusy: Boolean(publishingKey.value),
    fallbackLabel: "上线",
  });
  const publishStatus = matchedConfig?.publish_status || null;

  if (
    publishStatus?.state === "published" ||
    publishButtonState.label === "已上线"
  ) {
    return {
      ...base,
      state: "published",
      label: "已上线",
      color: "neutral",
      reason: publishButtonState.title || "当前测试数据与线上一致",
      matchedConfig,
      matchedConfigId,
      publishStatus,
    };
  }

  if (publishButtonState.disabled) {
    return {
      ...base,
      state: "blocked",
      label: publishButtonState.label || "状态异常",
      color: publishButtonState.color || "warning",
      reason: publishButtonState.title || "当前状态不允许上线",
      matchedConfig,
      matchedConfigId,
      publishStatus,
    };
  }

  return {
    ...base,
    state: "publishable",
    label: "上线",
    color: publishButtonState.color || "success",
    reason: publishButtonState.title || "",
    disabled: false,
    matchedConfig,
    matchedConfigId,
    publishStatus,
  };
}

const sourcePublishMetaMap = computed(() => {
  const out = {};
  for (const item of sources.value || []) {
    out[item.id] = buildSourcePublishMeta(item);
  }
  return out;
});

function sourcePublishMeta(item) {
  return sourcePublishMetaMap.value[item?.id] || buildSourcePublishMeta(item);
}

function canOpenSourceLinks(item) {
  return (
    ["success", "succeeded", "skipped", "cancelled"].includes(
      sourceDisplayStatus(item)
    ) && Boolean(sourceTemplateCode(item))
  );
}

const SOURCE_LINK_SITE_BASE = {
  test: "https://sharkfoto.zheng-yun.com",
  prod: "https://sharkfoto.com",
};
const SOURCE_LINK_CDN_BASE = {
  test: "https://pub-8f96a058cbeb4650ac7775f23dca06cc.r2.dev",
  prod: "https://cdn.sharkfoto.com",
};

function sourceProductPageUrl(item, env = "test") {
  const code = encodeURIComponent(sourceTemplateCode(item));
  const base = SOURCE_LINK_SITE_BASE[env] ?? SOURCE_LINK_SITE_BASE.test;
  return `${String(base).replace(/\/$/, "")}/ai/effects/video/${code}/`;
}

function sourceHtmlPageUrl(item, env = "test") {
  const code = encodeURIComponent(sourceTemplateCode(item));
  const base = SOURCE_LINK_CDN_BASE[env] ?? SOURCE_LINK_CDN_BASE.test;
  return `${String(base).replace(
    /\/$/,
    ""
  )}/ai/effects/video/html/${code}.html`;
}

function manusAppUrl(taskId) {
  const id = taskId != null ? String(taskId).trim() : "";
  return id ? `https://manus.im/app/${encodeURIComponent(id)}` : "";
}

const triggerMode = computed(() =>
  processKind.value === "manus_new_session" ? "full" : "partial"
);

const selectedCount = computed(() => selectedIds.value.length);
const selectedSources = computed(() =>
  sources.value.filter((item) => selectedIds.value.includes(item.id))
);
const selectedTemplateCodesPreview = computed(() => {
  const byId = new Map((sources.value || []).map((row) => [row.id, row]));
  return selectedIds.value.map((id) => {
    const row = byId.get(id);
    const code =
      row?.template_code != null ? String(row.template_code).trim() : "";
    return code || String(id ?? "");
  });
});
const selectedUpdateMissingManusCount = computed(() => {
  if (processKind.value !== "manus_update") {
    return 0;
  }
  return selectedSources.value.filter((item) => !item.latest_manus_task_id)
    .length;
});
const shouldShowUpdatePrompt = computed(
  () => processKind.value === "manus_update" && partialFlags.manus_enabled
);
const canCreateBatch = computed(
  () => selectedCount.value > 0 && !selectedUpdateMissingManusCount.value
);
const selectedSourcePublishMetas = computed(() =>
  selectedSources.value.map((item) => sourcePublishMeta(item))
);
const selectedPublishBlockReason = computed(() => {
  if (!canShowPublish.value) {
    return "";
  }
  if (effectsConfigsError.value) {
    return `上线状态加载失败，请刷新重试：${effectsConfigsError.value}`;
  }
  if (!selectedCount.value) {
    return "请先勾选任务";
  }
  if (
    selectedSourcePublishMetas.value.some(
      (meta) => meta.state === "not_success"
    )
  ) {
    return "选中项中存在非 success 任务，无法批量上线";
  }
  if (
    selectedSourcePublishMetas.value.some((meta) => meta.state === "not_synced")
  ) {
    return "选中项中存在未入库视频配置，无法批量上线";
  }
  if (
    selectedSourcePublishMetas.value.some((meta) => meta.state === "published")
  ) {
    return "选中项中存在已上线任务，无需重复上线";
  }
  const blockedMeta = selectedSourcePublishMetas.value.find(
    (meta) => meta.state === "blocked" || !meta.matchedConfigId
  );
  if (blockedMeta) {
    return blockedMeta.reason || "选中项中存在不可上线任务";
  }
  return "";
});
const canBatchPublishSelectedSources = computed(
  () => !!selectedCount.value && !selectedPublishBlockReason.value
);
const activeWorkflowNodeCount = computed(
  () =>
    workflowClickableNodeIds.filter(
      (nodeId) => partialFlags[workflowNodeFlagMap[nodeId]]
    ).length
);
const partialExecutionSummary = computed(() => {
  if (processKind.value !== "manus_update") {
    return "";
  }
  if (partialFlags.manus_enabled) {
    return "已勾选 Manus 处理：填写提示词则发送给已有 Manus task 并等待新回调；留空则直接使用数据库最新回调。随后会按该回调强制重建 workflow；其后 HTML / 文生图 / 首帧 / 尾帧 / 图生视频中，勾选项强制重新生成，未勾选项自动比对输入参数，没变化则复用历史产物。";
  }
  return "未勾选 Manus 处理：不会重新请求 Manus，而是复用历史 Manus 产物继续往下执行；首帧 / 尾帧只在 workflow 存在对应节点时执行，不存在则跳过。";
});

function setWorkflowNodes(value) {
  workflowClickableNodeIds.forEach((nodeId) => {
    partialFlags[workflowNodeFlagMap[nodeId]] = value;
  });
  syncWorkflowMermaidState();
}

function toggleWorkflowNodeStatus(nodeId) {
  if (!workflowNodeFlagMap[nodeId] && typeof window !== "undefined") {
    const event = window.event;
    const nodeElement = event?.target?.closest?.("g.node");
    if (nodeElement) {
      const matchedId = workflowClickableNodeIds.find((id) =>
        String(nodeElement.id || "")
          .split("-")
          .includes(id)
      );
      if (matchedId) {
        nodeId = matchedId;
      }
    }
  }
  const flag = workflowNodeFlagMap[nodeId];
  if (!flag) {
    return;
  }
  partialFlags[flag] = !partialFlags[flag];
  syncWorkflowMermaidState();
}

function ensureMermaidLoaded() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Mermaid can only load in browser"));
  }
  if (window.mermaid) {
    return Promise.resolve(window.mermaid);
  }
  if (mermaidLoadPromise) {
    return mermaidLoadPromise;
  }
  mermaidLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/mermaid/dist/mermaid.min.js";
    script.async = true;
    script.onload = () => resolve(window.mermaid);
    script.onerror = () => reject(new Error("Mermaid 加载失败"));
    document.head.appendChild(script);
  });
  return mermaidLoadPromise;
}

function syncWorkflowMermaidState() {
  const container = workflowMermaidContainer.value;
  if (!container) {
    return;
  }
  const renderedNodes = container.querySelectorAll("g.node");
  workflowClickableNodeIds.forEach((nodeId) => {
    const active = Boolean(partialFlags[workflowNodeFlagMap[nodeId]]);
    renderedNodes.forEach((node) => {
      const idParts = String(node.id || "").split("-");
      if (!idParts.includes(nodeId)) {
        return;
      }
      node.classList.toggle("clickable-active", active);
    });
  });
}

async function renderWorkflowMermaid() {
  await nextTick();
  const root = workflowMermaidRoot.value;
  if (!root) {
    return;
  }
  try {
    const mermaid = await ensureMermaidLoaded();
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "loose",
      theme: "base",
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        nodeSpacing: 24,
        rankSpacing: 34,
      },
    });
    window.toggleNodeStatus = toggleWorkflowNodeStatus;
    const renderId = `workflow-dag-${Date.now()}`;
    const { svg, bindFunctions } = await mermaid.render(
      renderId,
      workflowMermaidSource
    );
    root.innerHTML = svg;
    if (typeof bindFunctions === "function") {
      bindFunctions(root);
    }
    syncWorkflowMermaidState();
  } catch (error) {
    root.innerHTML = `<div class="text-sm text-red-500">${String(
      error?.message || error
    )}</div>`;
  }
}

const hasActiveRunTasks = computed(() => {
  const taskRunning = runTasks.value.some((item) =>
    ["queued", "running"].includes(item?.status)
  );
  const batchRunning = batches.value.some((item) =>
    ["queued", "running"].includes(item?.status)
  );
  return taskRunning || batchRunning;
});

const activeLogTask = computed(() => {
  if (!activeLogTaskId.value) {
    return null;
  }
  return (
    runTasks.value.find((item) => item.id === activeLogTaskId.value) || null
  );
});

const activeLogState = computed(
  () => logStates[activeLogTaskId.value] || createEmptyLogState()
);

const activeLogParsed = computed(() =>
  parseWorkflowLog(activeLogState.value.content)
);

const activeLogTimeline = computed(() => runTaskTimeline(activeLogTask.value));

const activeLogSelectedStep = computed(() => {
  const selectedKey = activeLogSelectedStepKey.value;
  return (
    activeLogTimeline.value.find((step) => step.key === selectedKey) ||
    activeLogTimeline.value.find((step) => step.record) ||
    activeLogTimeline.value[0] ||
    null
  );
});

const completedTimelineCount = computed(
  () =>
    activeLogTimeline.value.filter((step) =>
      ["done", "failed", "skipped", "reused"].includes(step.state)
    ).length
);

watch(hasActiveRunTasks, async (active, wasActive) => {
  if (wasActive === true && active === false) {
    await Promise.all([loadSources(), loadEffectsConfigs()]);
  }
});

watch(keyword, () => {
  sourcePage.value = 1;
  nextTick(scrollSourceListToTop);
});

watch(sourceStatusFilter, () => {
  sourcePage.value = 1;
  nextTick(scrollSourceListToTop);
});

watch(publishStatusFilter, () => {
  sourcePage.value = 1;
  nextTick(scrollSourceListToTop);
});

watch(sourceCategoryFilter, () => {
  sourcePage.value = 1;
  nextTick(scrollSourceListToTop);
});

watch(sourceNotesFilter, () => {
  sourcePage.value = 1;
  nextTick(scrollSourceListToTop);
});

watch(newCategoryName, (value) => {
  if (newCategoryIdTouched.value) {
    return;
  }
  newCategoryId.value = buildCategorySlug(value);
});

watch(batches, (items) => {
  const ids = new Set(items.map((item) => item.id));
  selectedBatchIds.value = selectedBatchIds.value.filter((id) => ids.has(id));
});

watch(sourceStatusItems, (items) => {
  const values = new Set(items.map((item) => item.value));
  if (!values.has(sourceStatusFilter.value)) {
    sourceStatusFilter.value = "all";
  }
});

watch(publishStatusItems, (items) => {
  const values = new Set(items.map((item) => item.value));
  if (!values.has(publishStatusFilter.value)) {
    publishStatusFilter.value = "all";
  }
});

watch(sourceCategoryItems, (items) => {
  const values = new Set(items.map((item) => item.value));
  if (!values.has(sourceCategoryFilter.value)) {
    sourceCategoryFilter.value = "all";
  }
});

watch(sourceNotesItems, (items) => {
  const values = new Set(items.map((item) => item.value));
  if (!values.has(sourceNotesFilter.value)) {
    sourceNotesFilter.value = "all";
  }
});

watch(sourcePageCount, (count) => {
  if (sourcePage.value > count) {
    sourcePage.value = count;
  }
  if (sourcePage.value < 1) {
    sourcePage.value = 1;
  }
});

watch(workflowDagOpen, (open) => {
  if (open) {
    renderWorkflowMermaid();
  }
});

watch(
  () => [
    partialFlags.manus_enabled,
    partialFlags.html_enabled,
    partialFlags.bgm_enabled,
    partialFlags.image_enabled,
    partialFlags.first_frame_enabled,
    partialFlags.tail_frame_enabled,
    partialFlags.video_enabled,
  ],
  () => syncWorkflowMermaidState()
);

watch(
  () => partialFlags.image_enabled,
  (value) => {
    if (value) {
      partialFlags.first_frame_enabled = true;
      partialFlags.tail_frame_enabled = true;
      partialFlags.video_enabled = true;
    }
  }
);

watch(
  () => [partialFlags.first_frame_enabled, partialFlags.tail_frame_enabled],
  ([firstFrameEnabled, tailFrameEnabled]) => {
    if (firstFrameEnabled || tailFrameEnabled) {
      partialFlags.video_enabled = true;
    }
  }
);

function formatJson(value) {
  if (!value) {
    return "暂无该步骤输入参数";
  }
  try {
    return JSON.stringify(value, null, 4);
  } catch (error) {
    return String(value);
  }
}

function formatStepPayload(value, emptyText) {
  if (value === null || value === undefined || value === "") {
    return emptyText;
  }
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    !Object.keys(value).length
  ) {
    return emptyText;
  }
  try {
    return JSON.stringify(value, null, 4);
  } catch (error) {
    return String(value);
  }
}

function createEmptyLogState() {
  return {
    content: "",
    next_offset: 0,
    file_size: 0,
    created_at: "",
    updated_at: "",
    fetched_at: "",
  };
}

function stepLabel(stepKey) {
  return workflowStepDefinitionMap[stepKey]?.label || stepKey || "-";
}

function hasLoadedTaskSteps(task) {
  return Array.isArray(task?.steps) && task.steps.length > 0;
}

function stepRecordMap(task) {
  const out = {};
  const steps = Array.isArray(task?.steps) ? task.steps : [];
  for (const record of steps) {
    if (!record?.step_key) {
      continue;
    }
    out[record.step_key] = record;
  }
  return out;
}

function timelineStateForStep(task, stepKey, record) {
  if (record?.status === "failed") {
    return "failed";
  }
  if (record?.status === "skipped") {
    return "skipped";
  }
  if (record?.status === "succeeded") {
    return record?.trigger_source === "reuse" ? "reused" : "done";
  }
  if (task?.status === "running" && task?.current_step === stepKey) {
    return "running";
  }
  if (
    ["failed", "cancelled"].includes(task?.status) &&
    task?.current_step === stepKey
  ) {
    return task.status;
  }
  const currentIndex = workflowStepIndexMap[task?.current_step];
  const stepIndex = workflowStepIndexMap[stepKey];
  if (Number.isInteger(currentIndex) && Number.isInteger(stepIndex)) {
    if (["first_frame", "tail_frame"].includes(task?.current_step)) {
      const firstParallelIndex = workflowStepIndexMap.first_frame;
      if (stepIndex < firstParallelIndex) {
        return "done";
      }
      return "pending";
    }
    if (task?.status === "succeeded" && stepIndex <= currentIndex) {
      return "done";
    }
    if (
      ["running", "failed", "cancelled"].includes(task?.status) &&
      stepIndex < currentIndex
    ) {
      return "done";
    }
  }
  if (task?.status === "succeeded" && !task?.current_step) {
    return "done";
  }
  return "pending";
}

function stepTriggerLabel(record) {
  if (!record) {
    return "";
  }
  if (record.trigger_source === "reuse") {
    return "复用历史";
  }
  if (record.trigger_source === "skip") {
    return "跳过";
  }
  if (record.forced_execute) {
    return "强制执行";
  }
  if (record.trigger_source === "execute") {
    return "执行";
  }
  if (record.trigger_source === "forced") {
    return "强制执行";
  }
  return record.trigger_source || "";
}

function formatDurationMs(value) {
  const ms = Number(value || 0);
  if (!ms) {
    return "";
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

function runTaskTimeline(task) {
  const records = stepRecordMap(task);
  const logStepsByLabel = activeLogParsed.value?.stepsByLabel || {};
  return workflowStepDefinitions.map((definition) => {
    const record = records[definition.key] || null;
    const label = record?.step_label || definition.label;
    const logStep = logStepsByLabel[label] || null;
    return {
      ...definition,
      label,
      shortLabel:
        definition.shortLabel || record?.step_label || definition.label,
      state: timelineStateForStep(task, definition.key, record),
      record,
      triggerLabel: stepTriggerLabel(record),
      started_at: record?.started_at || logStep?.started_at || "",
      finished_at: record?.finished_at || logStep?.finished_at || "",
      durationLabel: formatDurationMs(record?.duration_ms),
      input_payload: record?.input_payload || logStep?.input_payload,
      output_payload: record?.output_payload || logStep?.output_payload,
      error_message: record?.error_message || "",
    };
  });
}

function timelineStepClass(state) {
  const map = {
    done: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15",
    reused:
      "border-cyan-500/30 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/15",
    running:
      "border-primary/50 bg-primary/15 text-cyan-100 hover:bg-primary/20",
    failed: "border-red-500/40 bg-red-500/10 text-red-100 hover:bg-red-500/15",
    cancelled:
      "border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15",
    skipped: "border-gray-600 bg-gray-800/70 text-gray-300 hover:bg-gray-800",
    pending:
      "border-gray-800 bg-gray-950/50 text-gray-500 hover:bg-gray-900/70",
  };
  return map[state] || map.pending;
}

function timelineDotClass(state) {
  const map = {
    done: "bg-emerald-400",
    reused: "bg-cyan-400",
    running: "bg-primary animate-pulse",
    failed: "bg-red-400",
    cancelled: "bg-amber-400",
    skipped: "bg-gray-500",
    pending: "bg-gray-700",
  };
  return map[state] || map.pending;
}

function timelineBadgeColor(state) {
  const map = {
    done: "success",
    reused: "info",
    running: "primary",
    failed: "error",
    cancelled: "warning",
    skipped: "neutral",
    pending: "neutral",
  };
  return map[state] || "neutral";
}

function timelineStateLabel(state) {
  const map = {
    done: "完成",
    reused: "复用",
    running: "运行中",
    failed: "失败",
    cancelled: "已取消",
    skipped: "跳过",
    pending: "待执行",
  };
  return map[state] || state || "-";
}

function normalizeLogLines(content) {
  const lines = String(content || "").split(/\r?\n/);
  const entries = [];
  const linePattern =
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[(INFO|WARNING|ERROR)\] ?(.*)$/;
  for (const line of lines) {
    const matched = line.match(linePattern);
    if (matched) {
      entries.push({
        time: matched[1],
        level: matched[2],
        message: matched[3] || "",
      });
      continue;
    }
    if (entries.length) {
      entries[entries.length - 1].message = `${
        entries[entries.length - 1].message
      }\n${line}`;
    } else if (line.trim()) {
      entries.push({ time: "", level: "INFO", message: line });
    }
  }
  return entries;
}

function isStepBoundaryMessage(message) {
  const text = String(message || "");
  return (
    text.includes("══════════") ||
    text.startsWith("📥 输入参数") ||
    text.startsWith("📦 输出产物") ||
    text.startsWith("📦 复用产物") ||
    /^.+ 复用历史结果$/.test(text.split("\n")[0] || "") ||
    /^.+ 输入参数:/.test(text.split("\n")[0] || "") ||
    /^.+ 复用产物:/.test(text.split("\n")[0] || "") ||
    /^.+ 输出产物:/.test(text.split("\n")[0] || "")
  );
}

function parseLogJsonPayload(text) {
  const raw = String(text || "").trim();
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    return raw;
  }
}

function ensureParsedLogStep(stepsByLabel, label) {
  const normalizedLabel = String(label || "").trim();
  if (!normalizedLabel) {
    return null;
  }
  if (!stepsByLabel[normalizedLabel]) {
    stepsByLabel[normalizedLabel] = {
      label: normalizedLabel,
      input_payload: null,
      output_payload: null,
      started_at: "",
      finished_at: "",
    };
  }
  return stepsByLabel[normalizedLabel];
}

function extractStepStartLabel(message) {
  const matched = String(message || "").match(/步骤: ([^|═]+?)(?:\s*\||\s*═)/);
  return matched ? matched[1].trim() : "";
}

function extractStepEndLabel(message) {
  const matched = String(message || "").match(
    /步骤(?:完成|结束): ([^═]+?)\s*═/
  );
  return matched ? matched[1].trim() : "";
}

function extractLegacyStepLabel(message, marker) {
  const firstLine = String(message || "").split("\n")[0] || "";
  const index = firstLine.indexOf(marker);
  return index > 0 ? firstLine.slice(0, index).trim() : "";
}

function parseWorkflowLog(content) {
  const entries = normalizeLogLines(content);
  const events = [];
  const stepsByLabel = {};
  let currentStepLabel = "";
  for (const entry of entries) {
    const message = String(entry.message || "").trim();
    if (!message) {
      continue;
    }
    const stepStartLabel = extractStepStartLabel(message);
    if (stepStartLabel) {
      currentStepLabel = stepStartLabel;
      const step = ensureParsedLogStep(stepsByLabel, currentStepLabel);
      if (step && !step.started_at) {
        step.started_at = entry.time;
      }
      continue;
    }
    const stepEndLabel = extractStepEndLabel(message);
    if (stepEndLabel) {
      const step = ensureParsedLogStep(stepsByLabel, stepEndLabel);
      if (step) {
        step.finished_at = entry.time;
      }
      currentStepLabel = "";
      continue;
    }
    if (message.startsWith("📥 输入参数")) {
      const step = ensureParsedLogStep(stepsByLabel, currentStepLabel);
      if (step) {
        step.input_payload = parseLogJsonPayload(
          message.replace(/^📥 输入参数:\s*/, "")
        );
      }
      continue;
    }
    if (
      message.startsWith("📦 输出产物") ||
      message.startsWith("📦 复用产物")
    ) {
      const step = ensureParsedLogStep(stepsByLabel, currentStepLabel);
      if (step) {
        step.output_payload = parseLogJsonPayload(
          message.replace(/^📦 (?:输出产物|复用产物):\s*/, "")
        );
      }
      continue;
    }
    const legacyInputLabel = extractLegacyStepLabel(message, "输入参数:");
    if (legacyInputLabel) {
      const step = ensureParsedLogStep(stepsByLabel, legacyInputLabel);
      if (step) {
        step.input_payload = parseLogJsonPayload(
          message.replace(/^.+?输入参数:\s*/, "")
        );
      }
      continue;
    }
    const legacyOutputLabel =
      extractLegacyStepLabel(message, "输出产物:") ||
      extractLegacyStepLabel(message, "复用产物:");
    if (legacyOutputLabel) {
      const step = ensureParsedLogStep(stepsByLabel, legacyOutputLabel);
      if (step) {
        step.output_payload = parseLogJsonPayload(
          message.replace(/^.+?(?:输出产物|复用产物):\s*/, "")
        );
      }
      continue;
    }
    if (isStepBoundaryMessage(message)) {
      continue;
    }
    events.push({
      time: entry.time,
      level: entry.level,
      message,
    });
  }
  return { entries, events, stepsByLabel };
}

function openAssetDetail(tile, source = null) {
  Object.assign(assetDetail, {
    label: tile?.label || "",
    url: tile?.url || "",
    isVideo: Boolean(tile?.isVideo),
    inputPayload: tile?.inputPayload || null,
    source,
    manusVideoSource: tile?.manusVideoSource || "",
    manusSelectable: Boolean(tile?.manusSelectable),
  });
  assetDetailOpen.value = true;
}

function normalizeSource(item) {
  const sourceVideoUrl = normalizeText(item?.source_video_url);
  const sourceCoverUrl = normalizeText(item?.source_cover_url);
  let manusVideoSource = normalizeText(item?.manus_video_source);
  if (!manusVideoSource) {
    if (sourceCoverUrl && !sourceVideoUrl) {
      manusVideoSource = MANUS_VIDEO_SOURCE_COVER;
    } else if (sourceVideoUrl && !sourceCoverUrl) {
      manusVideoSource = MANUS_VIDEO_SOURCE_VIDEO;
    }
  }
  return {
    ...item,
    assets_num: Number(item.assets_num || 1),
    manus_video_source: manusVideoSource,
  };
}

/**
 * 传入时间戳并根据用户的地区自动格式化
 * @param {number|string|null|undefined} timestamp - 数字毫秒时间戳 或 日期字符串
 * @returns {string} 格式化后的日期时间字符串，无效值返回空字符串
 */
function formatDateTime(timestamp) {
  // 处理 null、undefined、空字符串
  if (timestamp === null || timestamp === undefined || timestamp === "") {
    return "";
  }

  // 获取用户的语言环境（默认使用浏览器语言）
  const userLocale =
    typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US";

  let date;

  // 情况1：数字时间戳（毫秒）
  if (typeof timestamp === "number") {
    // 检查数字是否有效（时间戳通常大于0）
    if (isNaN(timestamp) || timestamp <= 0) {
      return "";
    }
    date = new Date(timestamp);
  }
  // 情况2：字符串时间戳
  else if (typeof timestamp === "string") {
    const trimmed = timestamp.trim();

    // 空字符串
    if (trimmed === "") {
      return "";
    }

    // 检查是否已经是标准ISO格式（包含T和Z或时区）
    if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.test(
        trimmed
      )
    ) {
      // 标准ISO格式，直接解析
      date = new Date(trimmed + "Z");
    }
    // 检查是否是无时区的日期时间格式（如 "2025-03-28 10:30:00"）
    else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
      // 无时区字符串，当作UTC时间处理
      date = new Date(trimmed + "Z");
    }
    // 检查是否只是日期（如 "2025-03-28"）
    else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      date = new Date(trimmed + "T00:00:00Z");
    }
    // 其他字符串格式，让Date尝试解析
    else {
      date = new Date(trimmed);
    }
  }
  // 其他类型（boolean、object等）
  else {
    return "";
  }

  // 验证日期是否有效
  if (isNaN(date.getTime())) {
    return "";
  }

  // 根据用户的语言环境和时区进行格式化
  const formatter = new Intl.DateTimeFormat(userLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // console.log("测试用：" + timestamp + " 格式化后：" + formatter.format(date));
  return formatter.format(date);
}

function batchKindLabel(kind) {
  if (kind === "manus_new_session") {
    return "新会话全量生成";
  }
  if (kind === "manus_update") {
    return "已有会话增量执行";
  }
  return kind || "-";
}

function batchRunModeLabel(mode) {
  if (mode === "full") {
    return "全量执行";
  }
  if (mode === "partial") {
    return "按勾选执行";
  }
  return mode || "-";
}

function batchTaskTotal(batch) {
  const total = batch?.stats?.total;
  if (typeof total === "number" && !Number.isNaN(total)) {
    return total;
  }
  const s = batch?.stats || {};
  return (
    Number(s.queued || 0) +
    Number(s.running || 0) +
    Number(s.succeeded || 0) +
    Number(s.failed || 0) +
    Number(s.cancelled || 0) +
    Number(s.skipped || 0)
  );
}

function formatFileSize(size) {
  const num = Number(size || 0);
  if (!num) {
    return "0 B";
  }
  if (num < 1024) {
    return `${num} B`;
  }
  if (num < 1024 * 1024) {
    return `${(num / 1024).toFixed(1)} KB`;
  }
  if (num < 1024 * 1024 * 1024) {
    return `${(num / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${(num / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function isBatchCancelable(batch) {
  return ["queued", "running"].includes(batch?.status);
}

function isBatchPurgeable(batch) {
  return batch?.id && !["queued", "running"].includes(batch?.status);
}

function isTaskCancelable(task) {
  return ["queued", "running"].includes(task?.status);
}

function shouldShowCurrentStep(task) {
  return Boolean(
    task?.current_step && ["queued", "running"].includes(task?.status)
  );
}

function resetForm() {
  Object.assign(form, {
    id: "",
    effect_title: "",
    template_code: "",
    source_video_url: "",
    source_cover_url: "",
    manus_video_source: "",
    category_name: "",
    template_category: "",
    assets_num: 1,
    notes: "",
  });
  sourceActionMessage.value = "";
  sourceFormExpanded.value = false;
}

function openCreateForm() {
  Object.assign(form, {
    id: "",
    effect_title: "",
    template_code: "",
    source_video_url: "",
    source_cover_url: "",
    manus_video_source: "",
    category_name: "",
    template_category: "",
    assets_num: 1,
    notes: "",
  });
  sourceActionMessage.value = "";
  sourceFormExpanded.value = true;
}

function editSource(item) {
  Object.assign(form, {
    id: item.id,
    effect_title: item.effect_title || "",
    template_code: item.template_code || "",
    source_video_url: item.source_video_url || "",
    source_cover_url: item.source_cover_url || "",
    manus_video_source: item.manus_video_source || "",
    category_name: item.category_name || "",
    template_category: item.template_category || "",
    assets_num: Number(item.assets_num || 1),
    notes: item.notes || "",
  });
  sourceActionMessage.value = "";
  sourceFormExpanded.value = true;
}

function toggleSelected(id, checked) {
  if (checked) {
    if (!selectedIds.value.includes(id)) {
      selectedIds.value = [...selectedIds.value, id];
    }
    return;
  }
  selectedIds.value = selectedIds.value.filter((item) => item !== id);
}

function toggleSelectAll(checked) {
  if (checked) {
    selectedIds.value = filteredSources.value.map((item) => item.id);
    return;
  }
  selectedIds.value = [];
}

function sourceHasBothManusVideos(item) {
  return Boolean(
    normalizeText(item?.source_cover_url) &&
      normalizeText(item?.source_video_url)
  );
}

function sourceNeedsManusVideoSelection(item) {
  return (
    sourceHasBothManusVideos(item) && !normalizeText(item?.manus_video_source)
  );
}

async function setSourceManusVideoSource(item, sourceType, checked) {
  if (!item?.id) {
    return;
  }
  const nextValue = checked
    ? sourceType
    : sourceHasBothManusVideos(item)
    ? ""
    : sourceType;
  const previousValue = item.manus_video_source || "";
  if (previousValue === nextValue) {
    return;
  }
  savingManusVideoSourceMap[item.id] = true;
  item.manus_video_source = nextValue;
  try {
    const result = await WorkflowAdminService.updateTaskSource(item.id, {
      ...item,
      manus_video_source: nextValue,
    });
    if (result.status !== 200) {
      item.manus_video_source = previousValue;
      sourceActionMessage.value =
        result.message || result.error || "Manus 视频选择保存失败";
    }
  } catch (error) {
    item.manus_video_source = previousValue;
    sourceActionMessage.value = error?.message || "Manus 视频选择保存失败";
  } finally {
    savingManusVideoSourceMap[item.id] = false;
  }
}

function scrollSourceListToTop() {
  const el = sourceListRef.value;
  if (!el) {
    return;
  }
  if (typeof el.scrollTo === "function") {
    el.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  el.scrollTop = 0;
}

function onSourcePageChange(newPage) {
  const page = Number(newPage) || 1;
  sourcePage.value = Math.min(Math.max(page, 1), sourcePageCount.value);
  nextTick(scrollSourceListToTop);
}

async function refreshAll() {
  loadingAll.value = true;
  try {
    await Promise.all([
      loadSources(),
      loadCategories(),
      loadRunBatches(),
      loadRunTasks(),
      loadEffectsConfigs(),
    ]);
  } finally {
    loadingAll.value = false;
  }
}

async function loadEffectsConfigs() {
  if (!canShowPublish.value) {
    effectsConfigs.value = [];
    effectsConfigsError.value = "";
    return;
  }
  if (loadingEffectsConfigs.value) {
    return;
  }
  loadingEffectsConfigs.value = true;
  effectsConfigsError.value = "";
  try {
    const result = await $axios({
      method: "get",
      url: EFFECTS_CONFIG_API_PREFIX,
      baseURL: runtimeConfig.public.apiBase,
      params: publishStatusQuery.value,
    });
    effectsConfigs.value = Array.isArray(result?.data) ? result.data : [];
  } catch (error) {
    effectsConfigs.value = [];
    effectsConfigsError.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "effects-configs 加载失败";
  } finally {
    loadingEffectsConfigs.value = false;
  }
}

async function loadCategories() {
  if (loadingCategories.value) {
    return;
  }
  loadingCategories.value = true;
  try {
    const result = await $axios({
      method: "get",
      url: CATEGORIES_API_PREFIX,
      baseURL: runtimeConfig.public.apiBase,
      params: publishStatusQuery.value,
    });
    const rows = Array.isArray(result?.data) ? result.data : [];
    const scoped = rows.filter(
      (item) =>
        normalizeText(item.group_name || item.groupName) ===
        WORKFLOW_EFFECTS_CATEGORY_GROUP
    );
    categories.value = scoped.map((item) => ({
      id: item.id,
      templateCategory: normalizeText(item.category_id || item.categoryId),
      categoryName: normalizeText(item.category_name || item.categoryName),
      groupName: normalizeText(item.group_name || item.groupName),
      publish_status: item.publish_status,
    }));
  } finally {
    loadingCategories.value = false;
  }
}

async function loadSources() {
  loadingSources.value = true;
  try {
    const result = await WorkflowAdminService.getTaskSources();
    if (result.status === 200) {
      sources.value = Array.isArray(result.data)
        ? result.data.map(normalizeSource)
        : [];
    }
  } finally {
    loadingSources.value = false;
  }
}

async function loadRunBatches() {
  loadingBatches.value = true;
  try {
    const result = await WorkflowAdminService.getRunBatches();
    if (result.status === 200) {
      batches.value = Array.isArray(result.data) ? result.data : [];
    }
  } finally {
    loadingBatches.value = false;
  }
}

async function loadRunTasks() {
  loadingRunTasks.value = true;
  try {
    const params = {};
    if (activeBatchId.value) {
      params.batch_id = activeBatchId.value;
    }
    const result = await WorkflowAdminService.getRunTasks(params);
    if (result.status === 200) {
      const previousById = new Map(
        runTasks.value.map((item) => [item.id, item])
      );
      runTasks.value = (Array.isArray(result.data) ? result.data : []).map(
        (item) => {
          const previous = previousById.get(item.id);
          return previous?.steps
            ? {
                ...item,
                steps: previous.steps,
                asset: item.asset || previous.asset || null,
              }
            : item;
        }
      );
      const ids = new Set(runTasks.value.map((t) => t.id));
      Object.keys(logStates).forEach((id) => {
        if (!ids.has(id)) {
          delete logStates[id];
          delete logFetchLoading[id];
          delete logContentRefs[id];
          delete logDetailLoaded[id];
          if (activeLogTaskId.value === id) {
            closeLogModal();
          }
        }
      });
      runTasksLoadedOnce.value = true;
    }
  } finally {
    loadingRunTasks.value = false;
  }
  syncPollingState();
}

async function loadRunTaskDetail(id) {
  const result = await WorkflowAdminService.getRunTask(id);
  if (result.status !== 200 || !result.data) {
    return;
  }
  runTasks.value = runTasks.value.map((item) =>
    item.id === id
      ? { ...item, ...result.data, asset: result.data.asset || null }
      : item
  );
  if (!activeLogSelectedStepKey.value) {
    const firstRecord = Array.isArray(result.data.steps)
      ? result.data.steps[0]
      : null;
    activeLogSelectedStepKey.value =
      firstRecord?.step_key || workflowStepDefinitions[0]?.key || "";
  }
}

async function refreshTaskDetail(id) {
  await loadRunTaskDetail(id);
  if (logModalOpen.value && activeLogTaskId.value === id) {
    await loadLogs(id, true);
  }
}

async function saveSource() {
  if (!form.effect_title) {
    sourceActionMessage.value = "名称必填";
    return;
  }
  if (
    !normalizeText(form.source_video_url) &&
    !normalizeText(form.source_cover_url)
  ) {
    sourceActionMessage.value = "原作品视频和源封面视频至少填写一个";
    return;
  }
  const selectedCategories = normalizeCsvValues(form.template_category);
  if (!selectedCategories.length) {
    sourceActionMessage.value = "请至少选择一个分类，如需新增请点「管理分类」";
    return;
  }
  const templateCode = resolveFormTemplateCode();
  if (!templateCode) {
    sourceActionMessage.value = "请填写 Effect 名称或 Template Code";
    return;
  }
  if (!form.id) {
    const duplicate = findDuplicateTemplateCodeSource(templateCode);
    if (duplicate) {
      sourceActionMessage.value = `template_code「${templateCode}」已存在（${
        duplicate.effect_title || "未命名"
      }），请修改后再提交`;
      return;
    }
  }
  savingSource.value = true;
  sourceActionMessage.value = "";
  const payload = { ...form, template_code: templateCode };
  try {
    const result = form.id
      ? await WorkflowAdminService.updateTaskSource(form.id, payload)
      : await WorkflowAdminService.createTaskSource(payload);
    if (result.status === 200) {
      sourceActionMessage.value = form.id ? "保存成功" : "新增成功";
      resetForm();
      await Promise.all([loadSources(), loadCategories()]);
    } else {
      sourceActionMessage.value = result.message || "保存失败";
    }
  } finally {
    savingSource.value = false;
  }
}

async function deleteSource(item) {
  deletingSourceId.value = item.id;
  try {
    const result = await WorkflowAdminService.deleteTaskSource(item.id);
    if (result.status === 200) {
      selectedIds.value = selectedIds.value.filter((id) => id !== item.id);
      if (form.id === item.id) {
        resetForm();
      }
      await loadSources();
    }
  } finally {
    deletingSourceId.value = "";
  }
}

async function publishSource(item) {
  const meta = sourcePublishMeta(item);
  if (meta.disabled || !meta.matchedConfigId) {
    return;
  }
  const result = await publishItem(
    EFFECTS_PUBLISH_RESOURCE,
    meta.matchedConfigId,
    `${EFFECTS_PUBLISH_RESOURCE}:workflow:${item.id}`
  );
  if (result) {
    await loadEffectsConfigs();
  }
}

async function batchPublishSelectedSources() {
  if (selectedPublishBlockReason.value) {
    batchActionMessage.value = selectedPublishBlockReason.value;
    return;
  }
  const ids = selectedSourcePublishMetas.value
    .map((meta) => meta.matchedConfigId)
    .filter(Boolean);
  if (!ids.length) {
    batchActionMessage.value = "选中项中没有可上线的视频配置";
    return;
  }
  batchActionMessage.value = "";
  const result = await publishItems(
    EFFECTS_PUBLISH_RESOURCE,
    ids,
    `${EFFECTS_PUBLISH_RESOURCE}:workflow-batch`,
    {
      confirmMessage: `确认上线选中的 ${ids.length} 项到生产环境？`,
    }
  );
  if (result) {
    await loadEffectsConfigs();
  }
}

async function createBatch() {
  if (!selectedIds.value.length) {
    batchActionMessage.value = "请先勾选任务";
    return;
  }
  if (
    processKind.value === "manus_update" &&
    selectedUpdateMissingManusCount.value
  ) {
    batchActionMessage.value =
      "已有会话增量执行要求所选任务都已有 Manus Task id";
    return;
  }
  const missingManusVideoSources = selectedSources.value.filter(
    sourceNeedsManusVideoSelection
  );
  if (missingManusVideoSources.length) {
    batchActionMessage.value = `请先为 ${missingManusVideoSources.length} 个任务选择发送给 Manus 的视频（源封面或原作品）`;
    return;
  }
  creatingBatch.value = true;
  batchActionMessage.value = "";
  try {
    const result = await WorkflowAdminService.createRunBatch({
      source_ids: selectedIds.value,
      process_kind: processKind.value,
      trigger_mode: triggerMode.value,
      concurrency_limit: Number(concurrencyLimit.value || 5),
      update_prompt: updatePrompt.value,
      partial_flags: partialFlags,
      created_by: "workflow_admin",
    });
    if (result.status === 200) {
      batchActionMessage.value = `批次已创建：${result.data?.id || ""}`;
      activeBatchId.value = result.data?.id || "";
      runTaskBatchFilterDraft.value = activeBatchId.value;
      selectedIds.value = [];
      await Promise.all([loadRunBatches(), loadRunTasks(), loadSources()]);
      syncPollingState();
    } else {
      batchActionMessage.value = result.message || "批次创建失败";
    }
  } finally {
    creatingBatch.value = false;
  }
}

const purgeableBatches = computed(() =>
  batches.value.filter((item) => isBatchPurgeable(item))
);

const selectedPurgeableBatchCount = computed(
  () =>
    selectedBatchIds.value.filter((id) =>
      purgeableBatches.value.some((item) => item.id === id)
    ).length
);

const isAllPurgeableBatchesSelected = computed(() => {
  const purgeableIds = purgeableBatches.value.map((item) => item.id);
  if (!purgeableIds.length) {
    return false;
  }
  return purgeableIds.every((id) => selectedBatchIds.value.includes(id));
});

function toggleBatchSelected(id, checked) {
  if (checked) {
    if (!selectedBatchIds.value.includes(id)) {
      selectedBatchIds.value = [...selectedBatchIds.value, id];
    }
    return;
  }
  selectedBatchIds.value = selectedBatchIds.value.filter((item) => item !== id);
}

function toggleSelectAllBatches(checked) {
  if (checked) {
    selectedBatchIds.value = purgeableBatches.value.map((item) => item.id);
    return;
  }
  selectedBatchIds.value = [];
}

function clearPurgedBatchSelection(purgedIds = []) {
  if (!purgedIds.length) {
    return;
  }
  const purgedSet = new Set(purgedIds);
  selectedBatchIds.value = selectedBatchIds.value.filter(
    (id) => !purgedSet.has(id)
  );
  if (activeBatchId.value && purgedSet.has(activeBatchId.value)) {
    activeBatchId.value = "";
    runTaskBatchFilterDraft.value = "";
  }
}

function buildBatchPurgeResultMessage(result) {
  const purged = Array.isArray(result?.purged) ? result.purged : [];
  const failed = Array.isArray(result?.failed) ? result.failed : [];
  if (purged.length && !failed.length) {
    return `已清理 ${purged.length} 个批次`;
  }
  if (purged.length && failed.length) {
    const failedText = failed
      .map((item) => `${item.id || "-"}: ${item.error || "清理失败"}`)
      .join("；");
    return `已清理 ${purged.length} 个批次，${failed.length} 个失败：${failedText}`;
  }
  if (failed.length) {
    return failed
      .map((item) => `${item.id || "-"}: ${item.error || "清理失败"}`)
      .join("；");
  }
  return "批量清理失败";
}

async function purgeSelectedBatches() {
  const batchIds = selectedBatchIds.value.filter((id) =>
    purgeableBatches.value.some((item) => item.id === id)
  );
  if (!batchIds.length) {
    batchActionMessage.value = "请先勾选可清理的批次";
    return;
  }
  openConfirmModal(
    `确认批量清理 ${batchIds.length} 个批次？将删除这些批次的运行任务卡片与日志记录（不含步骤产物与成品元数据表）。`,
    async () => {
      batchPurging.value = true;
      batchActionMessage.value = "";
      try {
        const result = await WorkflowAdminService.purgeRunBatches(batchIds);
        const payload = result.data || {};
        if (result.status === 200) {
          batchActionMessage.value = buildBatchPurgeResultMessage(payload);
          clearPurgedBatchSelection(payload.purged || batchIds);
          await Promise.all([loadRunBatches(), loadRunTasks(), loadSources()]);
          syncPollingState();
        } else {
          batchActionMessage.value =
            buildBatchPurgeResultMessage(payload) ||
            result.message ||
            "批量清理失败";
        }
      } finally {
        batchPurging.value = false;
      }
    }
  );
}

function purgeBatch(batch) {
  if (!batch?.id) {
    return;
  }
  openConfirmModal(
    `确认清理批次 ${batch.id}？将删除该批次的运行任务卡片与日志记录（不含步骤产物与成品元数据表）。`,
    async () => {
      purgingBatchId.value = batch.id;
      batchActionMessage.value = "";
      try {
        const result = await WorkflowAdminService.purgeRunBatch(batch.id);
        if (result.status === 200) {
          batchActionMessage.value = `已清理批次：${batch.id}`;
          clearPurgedBatchSelection([batch.id]);
          await Promise.all([loadRunBatches(), loadRunTasks(), loadSources()]);
          syncPollingState();
        } else {
          batchActionMessage.value = result.message || "清理批次失败";
        }
      } finally {
        purgingBatchId.value = "";
      }
    }
  );
}

function cancelBatch(batch) {
  if (!batch?.id || batch.cancel_requested) {
    return;
  }
  openConfirmModal(`确认终止批次 ${batch.id} 吗？`, async () => {
    cancellingBatchId.value = batch.id;
    batchActionMessage.value = "";
    try {
      const result = await WorkflowAdminService.cancelRunBatch(batch.id);
      if (result.status === 200) {
        batchActionMessage.value = `已发起终止批次：${batch.id}`;
        await Promise.all([loadRunBatches(), loadRunTasks()]);
        syncPollingState();
      } else {
        batchActionMessage.value = result.message || "终止批次失败";
      }
    } finally {
      cancellingBatchId.value = "";
    }
  });
}

function cancelTask(task) {
  if (!task?.id || task.cancel_requested) {
    return;
  }
  openConfirmModal(`确认终止任务 ${task.id} 吗？`, async () => {
    cancellingTaskId.value = task.id;
    batchActionMessage.value = "";
    try {
      const result = await WorkflowAdminService.cancelRunTask(task.id);
      if (result.status === 200) {
        batchActionMessage.value = `已发起终止任务：${task.id}`;
        await Promise.all([loadRunBatches(), loadRunTasks()]);
        if (logModalOpen.value && activeLogTaskId.value === task.id) {
          await loadLogs(task.id, true);
        }
        syncPollingState();
      } else {
        batchActionMessage.value = result.message || "终止任务失败";
      }
    } finally {
      cancellingTaskId.value = "";
    }
  });
}

function queryRunTasksByBatch() {
  activeBatchId.value = String(runTaskBatchFilterDraft.value || "").trim();
  loadRunTasks();
}

function resetRunTasksBatchFilter() {
  runTaskBatchFilterDraft.value = "";
  activeBatchId.value = "";
  loadRunTasks();
}

function focusBatch(id) {
  activeBatchId.value = id;
  runTaskBatchFilterDraft.value = id;
  loadRunTasks();
}

function taskHasLogDetailCache(id) {
  const task = runTasks.value.find((item) => item.id === id);
  return Boolean(
    logDetailLoaded[id] && hasLoadedTaskSteps(task) && logStates[id]
  );
}

async function openRunTaskLog(task, preferredStepKey = "") {
  if (!task?.id) {
    return;
  }
  activeLogTaskId.value = task.id;
  activeLogSelectedStepKey.value =
    preferredStepKey ||
    task.current_step ||
    workflowStepDefinitions[0]?.key ||
    "";
  logModalView.value = "parsed";
  if (!logStates[task.id]) {
    logStates[task.id] = createEmptyLogState();
  }
  logModalOpen.value = true;
  if (taskHasLogDetailCache(task.id)) {
    syncPollingState();
    return;
  }
  logDetailLoading.value = true;
  try {
    await Promise.all([loadRunTaskDetail(task.id), loadLogs(task.id, true)]);
    logDetailLoaded[task.id] = true;
  } finally {
    logDetailLoading.value = false;
    syncPollingState();
  }
}

async function refreshActiveLogContent() {
  const id = activeLogTaskId.value;
  if (!id) {
    return;
  }
  await loadLogs(id, true);
  logDetailLoaded[id] = true;
}

function closeLogModal() {
  logModalOpen.value = false;
  activeLogTaskId.value = "";
  activeLogSelectedStepKey.value = "";
  logModalView.value = "parsed";
  logDetailLoading.value = false;
  syncPollingState();
}

function onLogModalUpdateOpen(open) {
  if (!open) {
    activeLogTaskId.value = "";
    activeLogSelectedStepKey.value = "";
    logModalView.value = "parsed";
    logDetailLoading.value = false;
    syncPollingState();
  }
}

function setLogContentRef(id, el) {
  if (!id) {
    return;
  }
  if (el) {
    logContentRefs[id] = el;
    return;
  }
  delete logContentRefs[id];
}

/** 距底部小于此像素则视为「在看最新日志」，追加内容后自动滚到底 */
const LOG_SCROLL_STICKY_BOTTOM_PX = 80;
const LOG_CHUNK_LIMIT = 64 * 1024;
const LOG_MAX_REFILL_CHUNKS = 64;
const LOG_NO_PROGRESS_RETRY_LIMIT = 6;
const LOG_NO_PROGRESS_RETRY_INTERVAL_MS = 500;

function captureLogStickToBottom(el, reset) {
  if (reset || !el) {
    return { stickToBottom: true, savedScrollTop: 0 };
  }
  const sh = el.scrollHeight;
  const st = el.scrollTop;
  const ch = el.clientHeight;
  if (sh <= ch + 1) {
    return { stickToBottom: true, savedScrollTop: 0 };
  }
  const fromBottom = sh - st - ch;
  if (fromBottom <= LOG_SCROLL_STICKY_BOTTOM_PX) {
    return { stickToBottom: true, savedScrollTop: 0 };
  }
  return { stickToBottom: false, savedScrollTop: st };
}

async function loadLogs(id, reset = false) {
  if (!logModalOpen.value || activeLogTaskId.value !== id) {
    return;
  }
  if (logFetchLoading[id]) {
    return;
  }
  logFetchLoading[id] = true;
  try {
    const current = logStates[id] || createEmptyLogState();

    // 请求返回后再取样：避免 await 期间用户已向上滚动仍被当作「贴底」
    const { stickToBottom, savedScrollTop } = captureLogStickToBottom(
      logModalView.value === "raw" ? logContentRefs[id] : null,
      reset
    );

    let mergedContent = reset ? "" : current.content || "";
    let nextOffset = reset ? 0 : current.next_offset || 0;
    let fileSize = Number(current.file_size || 0);
    let createdAt = current.created_at || "";
    let updatedAt = current.updated_at || "";
    let refillCount = 0;
    let noProgressStreak = 0;

    // reset 时补齐全量日志（按块拉取直到 next_offset 到达 file_size）
    while (refillCount < LOG_MAX_REFILL_CHUNKS) {
      const offsetBefore = nextOffset;
      const result = await WorkflowAdminService.getRunTaskLogs(id, {
        offset: nextOffset,
        limit: LOG_CHUNK_LIMIT,
      });
      if (result.status !== 200) {
        break;
      }
      const data = result.data || {};
      const chunk = data.content || "";
      mergedContent = `${mergedContent}${chunk}`;
      nextOffset = Number(data.next_offset ?? offsetBefore);
      fileSize = Number(data.file_size ?? fileSize);
      createdAt = data.created_at || createdAt;
      updatedAt = data.updated_at || updatedAt;

      const hasMore = nextOffset < fileSize;
      const noProgress = nextOffset <= offsetBefore;
      if (!reset || !hasMore) {
        break;
      }
      if (noProgress) {
        noProgressStreak += 1;
        if (noProgressStreak >= LOG_NO_PROGRESS_RETRY_LIMIT) {
          break;
        }
        refillCount += 1;
        await new Promise((resolve) =>
          setTimeout(resolve, LOG_NO_PROGRESS_RETRY_INTERVAL_MS)
        );
        continue;
      }
      noProgressStreak = 0;
      refillCount += 1;
    }

    logStates[id] = {
      content: mergedContent,
      next_offset: nextOffset,
      file_size: fileSize,
      created_at: createdAt,
      updated_at: updatedAt,
      fetched_at: new Date().toISOString(),
    };

    await nextTick();
    const el = logContentRefs[id];
    if (el) {
      el.scrollTop = stickToBottom ? el.scrollHeight : savedScrollTop;
    }
    syncPollingState();
  } finally {
    logFetchLoading[id] = false;
  }
}

function shouldPollTaskLogs(taskId) {
  const task = runTasks.value.find((item) => item.id === taskId);
  if (!task) {
    return false;
  }
  return ["queued", "running"].includes(task?.status);
}

function hasStreamingExpandedLogs() {
  if (!logModalOpen.value || !activeLogTaskId.value) {
    return false;
  }
  if (!runTasksLoadedOnce.value) {
    return true;
  }
  return shouldPollTaskLogs(activeLogTaskId.value);
}

function syncPollingState() {
  const shouldPollLogs = hasStreamingExpandedLogs();
  if (hasActiveRunTasks.value || shouldPollLogs) {
    startPolling(hasActiveRunTasks.value, shouldPollLogs);
  } else {
    stopPolling();
  }
}

function startPolling(enableTaskPolling, enableLogPolling) {
  if (enableTaskPolling && !taskPollTimer) {
    taskPollTimer = setInterval(async () => {
      await loadRunTasks();
      syncPollingState();
    }, 5000);
  }
  if (enableTaskPolling && !batchPollTimer) {
    batchPollTimer = setInterval(async () => {
      await loadRunBatches();
      syncPollingState();
    }, 8000);
  }
  if (!enableTaskPolling && taskPollTimer) {
    clearInterval(taskPollTimer);
    taskPollTimer = null;
  }
  if (!enableTaskPolling && batchPollTimer) {
    clearInterval(batchPollTimer);
    batchPollTimer = null;
  }
  if (enableLogPolling && !logPollTimer) {
    logPollTimer = setInterval(async () => {
      if (
        logModalOpen.value &&
        activeLogTaskId.value &&
        shouldPollTaskLogs(activeLogTaskId.value)
      ) {
        await loadRunTaskDetail(activeLogTaskId.value);
        loadLogs(activeLogTaskId.value);
      }
    }, 3000);
  }
  if (!enableLogPolling && logPollTimer) {
    clearInterval(logPollTimer);
    logPollTimer = null;
  }
}

function stopPolling() {
  if (taskPollTimer) clearInterval(taskPollTimer);
  if (batchPollTimer) clearInterval(batchPollTimer);
  if (logPollTimer) clearInterval(logPollTimer);
  taskPollTimer = null;
  batchPollTimer = null;
  logPollTimer = null;
}

onMounted(async () => {
  await refreshAll();
  syncPollingState();
});

onBeforeUnmount(() => {
  stopPolling();
  if (
    typeof window !== "undefined" &&
    window.toggleNodeStatus === toggleWorkflowNodeStatus
  ) {
    delete window.toggleNodeStatus;
  }
});
</script>

<style scoped>
.workflow-mermaid-container :deep(.node .nodeLabel),
.workflow-mermaid-container :deep(.node text) {
  font-size: 13px !important;
  font-weight: 600 !important;
}

.workflow-mermaid-container :deep(.node foreignObject *) {
  font-size: 13px !important;
  font-weight: 600 !important;
}

.workflow-mermaid-container,
.workflow-mermaid-container :deep(svg),
.workflow-mermaid-container :deep(*) {
  user-select: none;
}

.workflow-mermaid-container :deep(svg) {
  max-height: 620px;
}

.workflow-mermaid-container :deep(.node rect) {
  rx: 8px !important;
  ry: 8px !important;
  fill: #e5e7eb !important;
  stroke: #d1d5db !important;
  stroke-width: 1px !important;
  transition: fill 0.2s;
}

.workflow-mermaid-container :deep(.node .nodeLabel) {
  fill: #6b7280 !important;
  color: #6b7280 !important;
  transition: fill 0.2s, color 0.2s;
}

.workflow-mermaid-container :deep(.node.node-clickable) {
  cursor: pointer !important;
}

.workflow-mermaid-container :deep(.node.node-clickable rect) {
  stroke: #3b82f6 !important;
  stroke-width: 2px !important;
}

.workflow-mermaid-container
  :deep(.node.node-clickable:not(.clickable-active) rect) {
  fill: #eff6ff !important;
}

.workflow-mermaid-container
  :deep(.node.node-clickable:not(.clickable-active) .nodeLabel) {
  fill: #1e40af !important;
  color: #1e40af !important;
}

.workflow-mermaid-container :deep(.node.node-clickable.clickable-active rect) {
  fill: #2563eb !important;
}

.workflow-mermaid-container
  :deep(.node.node-clickable.clickable-active .nodeLabel) {
  fill: #ffffff !important;
  color: #ffffff !important;
}

.workflow-mermaid-container :deep(.node-readonly rect) {
  fill: url(#workflow-stripe-pattern) !important;
  stroke: #94a3b8 !important;
  cursor: default !important;
}

.workflow-mermaid-container :deep(.node-readonly .nodeLabel) {
  fill: #334155 !important;
  color: #334155 !important;
}

.workflow-mermaid-container :deep(.node-start rect) {
  fill: #10b981 !important;
  stroke: #059669 !important;
  cursor: default !important;
}

.workflow-mermaid-container :deep(.node-start .nodeLabel) {
  fill: #ffffff !important;
  color: #ffffff !important;
}

.workflow-mermaid-container :deep(.node-notice rect) {
  fill: #f59e0b !important;
  stroke: #d97706 !important;
  cursor: default !important;
}

.workflow-mermaid-container :deep(.node-notice .nodeLabel) {
  fill: #ffffff !important;
  color: #ffffff !important;
}

.workflow-mermaid-container :deep(.node) {
  cursor: default;
}

.workflow-mermaid-container :deep(.edgePath .path) {
  stroke: #64748b !important;
}

.workflow-mermaid-container :deep(.arrowheadPath) {
  fill: #64748b !important;
}
</style>
